import React, { useState, useEffect, useRef, useMemo } from "react";
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    Button,
    Icon,
    Flex,
    Image,
    Progress,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    Link,
    SimpleGrid,
    Badge,
    Checkbox,
    Input,
    NumberInput,
    NumberInputField,
    Slider,
    SliderTrack,
    SliderFilledTrack,
    SliderThumb,
} from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { useNavigate } from "react-router-dom";
import { ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon, ExternalLinkIcon } from "@chakra-ui/icons";
import { FaUser, FaMoon, FaCloud, FaStar, FaBed, FaCheck, FaPlay, FaDownload } from "react-icons/fa";

const floatAnimation = keyframes`
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-8px); }
`;

const pulseAnimation = keyframes`
    0%, 100% { opacity: 0.6; }
    50% { opacity: 1; }
`;

const checkAnimation = keyframes`
    0% { transform: scale(0) rotate(-45deg); }
    50% { transform: scale(1.2) rotate(0deg); }
    100% { transform: scale(1) rotate(0deg); }
`;

const fadeSlideIn = keyframes`
    0% { opacity: 0; transform: translateY(10px); }
    100% { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
    0% { background-position: -200% center; }
    100% { background-position: 200% center; }
`;

const bookOpenAnimation = keyframes`
    0% { transform: rotateY(0deg) scale(1); }
    30% { transform: rotateY(-15deg) scale(1.02); }
    100% { transform: rotateY(-25deg) scale(1.05); }
`;

const pageFlipAnimation = keyframes`
    0% { transform: rotateY(0deg); opacity: 1; }
    100% { transform: rotateY(-180deg); opacity: 0; }
`;

const rapidPageFlip = keyframes`
    0% { transform: rotateY(0deg) translateX(0); }
    10% { transform: rotateY(-20deg) translateX(-2px); }
    20% { transform: rotateY(-5deg) translateX(0); }
    30% { transform: rotateY(-25deg) translateX(-3px); }
    40% { transform: rotateY(-10deg) translateX(-1px); }
    50% { transform: rotateY(-30deg) translateX(-4px); }
    60% { transform: rotateY(-15deg) translateX(-2px); }
    70% { transform: rotateY(-35deg) translateX(-5px); }
    80% { transform: rotateY(-20deg) translateX(-3px); }
    90% { transform: rotateY(-40deg) translateX(-6px); }
    100% { transform: rotateY(-180deg) translateX(-8px); opacity: 0; }
`;

const bookSpineGlow = keyframes`
    0%, 100% { box-shadow: 0 0 8px rgba(139, 92, 246, 0.4); }
    50% { box-shadow: 0 0 16px rgba(139, 92, 246, 0.7); }
`;

const drawCheck = keyframes`
    0% { stroke-dashoffset: 36; }
    100% { stroke-dashoffset: 0; }
`;

const circleFill = keyframes`
    0% { transform: scale(0.8); opacity: 0; }
    50% { transform: scale(1.1); }
    100% { transform: scale(1); opacity: 1; }
`;

const circleRing = keyframes`
    0% { stroke-dashoffset: 126; }
    100% { stroke-dashoffset: 0; }
`;

const arrowBob = keyframes`
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-3px); }
`;

const clockTick = keyframes`
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
`;

const clockTickMinute = keyframes`
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
`;

const talkingWave1 = keyframes`
    0%, 100% { transform: scaleX(0.6); opacity: 0.3; }
    50% { transform: scaleX(1); opacity: 0.8; }
`;

const talkingWave2 = keyframes`
    0%, 100% { transform: scaleX(0.5); opacity: 0.2; }
    50% { transform: scaleX(1); opacity: 0.6; }
`;

const talkingWave3 = keyframes`
    0%, 100% { transform: scaleX(0.4); opacity: 0.1; }
    50% { transform: scaleX(1); opacity: 0.5; }
`;

const cpapSuck1 = keyframes`
    0% { transform: translate(14px,-6px) rotate(-20deg) scaleX(0.6); opacity: 0; }
    30% { transform: translate(8px,-3px) rotate(-20deg) scaleX(1.2); opacity: 1; }
    70% { transform: translate(2px,-1px) rotate(-20deg) scaleX(0.8); opacity: 0.8; }
    100% { transform: translate(0,0) rotate(-20deg) scaleX(0); opacity: 0; }
`;

const cpapSuck2 = keyframes`
    0% { transform: translate(16px,0) rotate(0deg) scaleX(0.6); opacity: 0; }
    30% { transform: translate(9px,0) rotate(0deg) scaleX(1.2); opacity: 1; }
    70% { transform: translate(3px,0) rotate(0deg) scaleX(0.8); opacity: 0.8; }
    100% { transform: translate(0,0) rotate(0deg) scaleX(0); opacity: 0; }
`;

const cpapSuck3 = keyframes`
    0% { transform: translate(14px,6px) rotate(20deg) scaleX(0.6); opacity: 0; }
    30% { transform: translate(8px,3px) rotate(20deg) scaleX(1.2); opacity: 1; }
    70% { transform: translate(2px,1px) rotate(20deg) scaleX(0.8); opacity: 0.8; }
    100% { transform: translate(0,0) rotate(20deg) scaleX(0); opacity: 0; }
`;

const cpapPulse = keyframes`
    0%, 100% { opacity: 0.4; }
    50% { opacity: 0.8; }
`;

const speechBurst1 = keyframes`
    0% { transform: translate(0,0) rotate(-25deg) scaleX(0); opacity: 0; }
    20% { transform: translate(4px,-2px) rotate(-25deg) scaleX(1.3); opacity: 1; }
    60% { transform: translate(12px,-5px) rotate(-25deg) scaleX(1); opacity: 0.8; }
    100% { transform: translate(18px,-8px) rotate(-25deg) scaleX(0.6); opacity: 0; }
`;

const speechBurst2 = keyframes`
    0% { transform: translate(0,0) rotate(0deg) scaleX(0); opacity: 0; }
    20% { transform: translate(5px,0) rotate(0deg) scaleX(1.3); opacity: 1; }
    60% { transform: translate(14px,0) rotate(0deg) scaleX(1); opacity: 0.8; }
    100% { transform: translate(20px,0) rotate(0deg) scaleX(0.6); opacity: 0; }
`;

const speechBurst3 = keyframes`
    0% { transform: translate(0,0) rotate(25deg) scaleX(0); opacity: 0; }
    20% { transform: translate(4px,2px) rotate(25deg) scaleX(1.3); opacity: 1; }
    60% { transform: translate(12px,5px) rotate(25deg) scaleX(1); opacity: 0.8; }
    100% { transform: translate(18px,8px) rotate(25deg) scaleX(0.6); opacity: 0; }
`;

const AnimatedMoonIcon = ({ isVisible, delay }: { isVisible: boolean; delay: number }) => (
    <Box w="44px" h="44px" position="relative" display="flex" alignItems="center" justifyContent="center">
        <svg width="44" height="44" viewBox="0 0 44 44">
            <defs>
                <linearGradient id="moonGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#4A90E2" />
                    <stop offset="100%" stopColor="#2563EB" />
                </linearGradient>
            </defs>
            <circle cx="22" cy="22" r="20" fill="none" stroke="url(#moonGrad)" strokeWidth="2" 
                strokeDasharray="125.6" strokeDashoffset={isVisible ? "0" : "125.6"}
                style={{ transition: `stroke-dashoffset 0.8s ease ${delay}s` }} />
            <circle cx="22" cy="22" r="17" fill={isVisible ? "#4A90E2" : "transparent"} opacity={isVisible ? 0.12 : 0}
                style={{ transition: `all 0.5s ease ${delay + 0.3}s` }} />
            <path d="M22 10c-1.5 0-3 .3-4.3.9 3.8 1.5 6.3 5.2 6.3 9.1s-2.5 7.6-6.3 9.1c1.3.6 2.8.9 4.3.9 5.5 0 10-4.5 10-10s-4.5-10-10-10z"
                fill={isVisible ? "#4A90E2" : "transparent"} opacity={isVisible ? 1 : 0}
                style={{ transition: `all 0.6s ease ${delay + 0.4}s`, transformOrigin: "center", transform: isVisible ? "scale(1) rotate(0deg)" : "scale(0.3) rotate(-30deg)" }} />
        </svg>
    </Box>
);

const AnimatedStarIcon = ({ isVisible, delay }: { isVisible: boolean; delay: number }) => (
    <Box w="44px" h="44px" position="relative" display="flex" alignItems="center" justifyContent="center">
        <svg width="44" height="44" viewBox="0 0 44 44">
            <defs>
                <linearGradient id="starGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#3DA2DD" />
                    <stop offset="100%" stopColor="#7C3AED" />
                </linearGradient>
            </defs>
            <circle cx="22" cy="22" r="20" fill="none" stroke="url(#starGrad)" strokeWidth="2"
                strokeDasharray="125.6" strokeDashoffset={isVisible ? "0" : "125.6"}
                style={{ transition: `stroke-dashoffset 0.8s ease ${delay}s` }} />
            <circle cx="22" cy="22" r="17" fill={isVisible ? "#3DA2DD" : "transparent"} opacity={isVisible ? 0.12 : 0}
                style={{ transition: `all 0.5s ease ${delay + 0.3}s` }} />
            <path d="M22 11l2.5 5.5 6 .8-4.3 4.2 1 5.9L22 24.5l-5.2 2.9 1-5.9-4.3-4.2 6-.8z"
                fill={isVisible ? "#3DA2DD" : "transparent"} stroke={isVisible ? "#3DA2DD" : "transparent"} strokeWidth="0.5"
                strokeLinejoin="round" strokeLinecap="round"
                strokeDasharray="60" strokeDashoffset={isVisible ? "0" : "60"}
                style={{ transition: `all 0.7s ease ${delay + 0.3}s`, transformOrigin: "center", transform: isVisible ? "scale(1) rotate(0deg)" : "scale(0) rotate(-72deg)" }} />
        </svg>
    </Box>
);

const StaticArrowUpIcon = () => (
    <Box w="44px" h="44px" position="relative" display="flex" alignItems="center" justifyContent="center">
        <svg width="44" height="44" viewBox="0 0 44 44">
            <defs>
                <linearGradient id="arrowGrad" x1="0%" y1="100%" x2="0%" y2="0%">
                    <stop offset="0%" stopColor="#4A90E2" />
                    <stop offset="100%" stopColor="#22C55E" />
                </linearGradient>
            </defs>
            <circle cx="22" cy="22" r="20" fill="none" stroke="url(#arrowGrad)" strokeWidth="2" />
            <circle cx="22" cy="22" r="17" fill="#4A90E2" opacity={0.08} />
            <g>
                <animateTransform attributeName="transform" type="translate" values="0,0;0,-3;0,0" dur="2s" repeatCount="indefinite" />
                <line x1="22" y1="30" x2="22" y2="14" stroke="#4A90E2" strokeWidth="2.5" strokeLinecap="round" />
                <polyline points="15,20 22,13 29,20" fill="none" stroke="#4A90E2" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </g>
        </svg>
    </Box>
);

const StaticClockIcon = () => (
    <Box w="44px" h="44px" position="relative" display="flex" alignItems="center" justifyContent="center">
        <svg width="44" height="44" viewBox="0 0 44 44">
            <defs>
                <linearGradient id="clockGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#8B5CF6" />
                    <stop offset="100%" stopColor="#4A90E2" />
                </linearGradient>
            </defs>
            <circle cx="22" cy="22" r="20" fill="none" stroke="url(#clockGrad)" strokeWidth="2" />
            <circle cx="22" cy="22" r="17" fill="none" stroke="#8B5CF6" strokeWidth="1.5" opacity={0.3} />
            {[0,1,2,3,4,5,6,7,8,9,10,11].map(i => {
                const angle = (i * 30 - 90) * Math.PI / 180;
                const x1t = 22 + 14 * Math.cos(angle);
                const y1t = 22 + 14 * Math.sin(angle);
                const x2t = 22 + (i % 3 === 0 ? 11 : 12.5) * Math.cos(angle);
                const y2t = 22 + (i % 3 === 0 ? 11 : 12.5) * Math.sin(angle);
                return <line key={i} x1={x1t} y1={y1t} x2={x2t} y2={y2t}
                    stroke="#8B5CF6" strokeWidth={i % 3 === 0 ? "2" : "1"} strokeLinecap="round" opacity={0.5} />;
            })}
            <circle cx="22" cy="22" r="2" fill="#8B5CF6" />
            <line x1="22" y1="22" x2="22" y2="14" stroke="#8B5CF6" strokeWidth="2.5" strokeLinecap="round">
                <animateTransform attributeName="transform" type="rotate" from="0 22 22" to="360 22 22" dur="12s" repeatCount="indefinite" />
            </line>
            <line x1="22" y1="22" x2="28" y2="22" stroke="#8B5CF6" strokeWidth="2" strokeLinecap="round">
                <animateTransform attributeName="transform" type="rotate" from="0 22 22" to="360 22 22" dur="3s" repeatCount="indefinite" />
            </line>
        </svg>
    </Box>
);

const AnimatedSleepCard = ({ children, delay }: { children: React.ReactNode; delay: number }) => {
    const ref = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState(false);
    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) setIsVisible(true);
        }, { threshold: 0.3 });
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, []);
    return (
        <Box ref={ref} opacity={isVisible ? 1 : 0} transform={isVisible ? "translateY(0)" : "translateY(20px)"}
            transition={`all 0.6s ease ${delay}s`}>
            {React.Children.map(children, child =>
                React.isValidElement(child) ? React.cloneElement(child as React.ReactElement<any>, { isVisible, delay }) : child
            )}
        </Box>
    );
};

const HospitalChallengesBar = () => {
    const ref = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState(false);
    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) setIsVisible(true);
        }, { threshold: 0.2 });
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, []);
    return (
        <Box ref={ref} position="relative" overflow="hidden">
            <Box
                bg={isVisible ? "linear-gradient(135deg, #B45309 0%, #D97706 40%, #F59E0B 100%)" : "linear-gradient(135deg, #92400E 0%, #B45309 100%)"}
                borderRadius="xl"
                overflow="hidden"
                transform={isVisible ? "translateY(0)" : "translateY(40px)"}
                opacity={isVisible ? 1 : 0}
                maxH={isVisible ? "300px" : "0px"}
                transition="all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)"
                boxShadow={isVisible ? "0 8px 32px rgba(217, 119, 6, 0.3), 0 4px 16px rgba(0,0,0,0.1)" : "none"}
                position="relative"
            >
                <Box
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    bg="linear-gradient(180deg, rgba(255,255,255,0.08) 0%, transparent 40%, rgba(0,0,0,0.1) 100%)"
                    pointerEvents="none"
                />
                <Box p={6} position="relative">
                    <HStack mb={3} spacing={3}>
                        <Box position="relative" w="40px" h="40px" display="flex" alignItems="center" justifyContent="center">
                            <svg width="40" height="40" viewBox="0 0 40 40">
                                <path d="M20 6L4 34h32L20 6z" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round"
                                    strokeDasharray="80" strokeDashoffset={isVisible ? "0" : "80"}
                                    style={{ transition: "stroke-dashoffset 0.8s ease 0.3s" }} />
                                <path d="M20 6L4 34h32L20 6z" fill="rgba(255,255,255,0.12)"
                                    opacity={isVisible ? 1 : 0} style={{ transition: "opacity 0.4s ease 0.6s" }} />
                                <line x1="20" y1="16" x2="20" y2="24" stroke="rgba(255,255,255,0.9)" strokeWidth="2.5" strokeLinecap="round"
                                    strokeDasharray="8" strokeDashoffset={isVisible ? "0" : "8"}
                                    style={{ transition: "stroke-dashoffset 0.4s ease 0.7s" }} />
                                <circle cx="20" cy="28" r="1.5" fill="rgba(255,255,255,0.9)"
                                    opacity={isVisible ? 1 : 0} style={{ transition: "opacity 0.3s ease 0.9s" }} />
                            </svg>
                        </Box>
                        <VStack align="flex-start" spacing={0}>
                            <Text fontWeight="700" color="white" fontSize="lg" letterSpacing="-0.01em">
                                Sleep Challenges in the Hospital
                            </Text>
                            <Box h="2px" w={isVisible ? "100%" : "0%"} bg="rgba(255,255,255,0.4)" borderRadius="full" mt={1}
                                transition="width 0.6s ease 0.5s" />
                        </VStack>
                    </HStack>
                    <Text color="rgba(255,255,255,0.9)" lineHeight="1.8" fontSize="md"
                        opacity={isVisible ? 1 : 0} transform={isVisible ? "translateY(0)" : "translateY(10px)"}
                        transition="all 0.5s ease 0.4s">
                        Hospitalization poses significant challenges to sleep due to factors like noise, light, frequent interruptions, pain, and unfamiliar surroundings—all of which we can address with targeted actions.
                    </Text>
                </Box>
            </Box>
        </Box>
    );
};

const SleepTypeCard = ({ type, isVisible = false, delay = 0 }: { type: "moon" | "star"; isVisible?: boolean; delay?: number }) => {
    const isMoon = type === "moon";
    return (
        <Box bg="gray.50" borderRadius="xl" p={5} border="1px solid" borderColor={isVisible ? (isMoon ? "blue.200" : "purple.200") : "gray.200"}
            transition="all 0.4s ease" _hover={{ borderColor: isMoon ? "#4A90E2" : "#7C3AED", transform: "translateY(-2px)" }}
            boxShadow={isVisible ? `0 4px 20px ${isMoon ? "rgba(74, 144, 226, 0.12)" : "rgba(124, 58, 237, 0.12)"}` : "none"}>
            <HStack mb={3} spacing={3}>
                {isMoon ? <AnimatedMoonIcon isVisible={isVisible} delay={delay} /> : <AnimatedStarIcon isVisible={isVisible} delay={delay} />}
                <Text fontWeight="600" color="gray.700" fontSize="md">{isMoon ? "Deep (NREM) Sleep" : "REM Sleep"}</Text>
            </HStack>
            <Text color="gray.600" fontSize="sm" lineHeight="1.7">
                {isMoon ? "The body slows down, repairs tissues, restores energy, and supports the immune system."
                    : "The brain becomes more active, dreams occur, and important learning and memory processes take place."}
            </Text>
        </Box>
    );
};

const SleepSystemCard = ({ type }: { type: "pressure" | "clock"; isVisible?: boolean; delay?: number }) => {
    const isPressure = type === "pressure";
    return (
        <Box bg="white" borderRadius="xl" p={5} border="1px solid"
            borderColor={isPressure ? "blue.200" : "purple.200"}
            boxShadow={`0 4px 16px ${isPressure ? "rgba(74, 144, 226, 0.1)" : "rgba(139, 92, 246, 0.1)"}`}
            transition="all 0.3s ease" _hover={{ transform: "translateY(-2px)" }}>
            <HStack spacing={3} mb={2}>
                {isPressure ? <StaticArrowUpIcon /> : <StaticClockIcon />}
                <VStack align="flex-start" spacing={0}>
                    <Text fontWeight="600" color="gray.700">{isPressure ? "Sleep Pressure" : "The Body Clock"}</Text>
                    <Text color="gray.500" fontSize="sm">
                        {isPressure ? "Builds the longer we are awake, regulated by body chemicals like adenosine"
                            : "Helps time when we feel sleepy or alert, regulated by hormones like melatonin"}
                    </Text>
                </VStack>
            </HStack>
        </Box>
    );
};

const AnimatedMoonCircle = ({ text, delay }: { text: string; delay: number }) => {
    const ref = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState(false);
    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) setIsVisible(true);
        }, { threshold: 0.5 });
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, []);
    const uniqueId = `moonAssess_${delay}`;
    return (
        <HStack ref={ref} spacing={4} opacity={isVisible ? 1 : 0.3} transition={`opacity 0.5s ease ${delay}s`}>
            <Box w="36px" h="36px" minW="36px" position="relative" display="flex" alignItems="center" justifyContent="center">
                <svg width="36" height="36" viewBox="0 0 36 36">
                    <defs>
                        <linearGradient id={uniqueId} x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#3B82F6" />
                            <stop offset="50%" stopColor="#4A90E2" />
                            <stop offset="100%" stopColor="#1D4ED8" />
                        </linearGradient>
                    </defs>
                    <circle cx="18" cy="18" r="16" fill="none" stroke={isVisible ? "#4A90E2" : "#CBD5E0"} strokeWidth="2"
                        strokeDasharray="100.5" strokeDashoffset={isVisible ? "0" : "100.5"}
                        style={{ transition: `stroke-dashoffset 0.7s cubic-bezier(0.65, 0, 0.35, 1) ${delay}s, stroke 0.3s ease ${delay}s` }} />
                    <circle cx="18" cy="18" r="14" fill={isVisible ? `url(#${uniqueId})` : "transparent"}
                        opacity={isVisible ? 1 : 0}
                        transform={isVisible ? "scale(1)" : "scale(0)"}
                        style={{
                            transition: `transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) ${delay + 0.35}s, opacity 0.01s ease ${delay + 0.35}s`,
                            transformOrigin: "18px 18px"
                        }} />
                    <path d="M20.5 10.5c-0.8-0.3-1.6-0.5-2.5-0.5a8 8 0 1 0 0 16c0.9 0 1.7-0.2 2.5-0.5a6.5 6.5 0 0 1 0-15z"
                        fill="white" opacity={isVisible ? 0.95 : 0}
                        transform={isVisible ? "scale(1) rotate(0)" : "scale(0.5) rotate(-20deg)"}
                        style={{
                            transition: `all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) ${delay + 0.5}s`,
                            transformOrigin: "18px 18px"
                        }} />
                </svg>
            </Box>
            <Text color="gray.600" fontSize="md" lineHeight="1.6"
                transform={isVisible ? "translateX(0)" : "translateX(-6px)"}
                opacity={isVisible ? 1 : 0}
                transition={`all 0.4s ease ${delay + 0.4}s`}>
                {text}
            </Text>
        </HStack>
    );
};

const AnimatedCheckItem = ({ text, delay }: { text: string; delay: number }) => {
    const ref = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.5 }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, []);

    return (
        <HStack ref={ref} spacing={4} opacity={isVisible ? 1 : 0.3} transition="opacity 0.4s ease">
            <Box
                w="36px"
                h="36px"
                minW="36px"
                position="relative"
                display="flex"
                alignItems="center"
                justifyContent="center"
            >
                <svg width="36" height="36" viewBox="0 0 36 36">
                    <circle
                        cx="18"
                        cy="18"
                        r="16"
                        fill="none"
                        stroke={isVisible ? "#4A90E2" : "#CBD5E0"}
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeDasharray="100.5"
                        strokeDashoffset={isVisible ? "0" : "100.5"}
                        style={{
                            transition: `stroke-dashoffset ${0.6}s ease ${delay}s, stroke ${0.3}s ease ${delay}s`
                        }}
                    />
                    <circle
                        cx="18"
                        cy="18"
                        r="14"
                        fill={isVisible ? "#4A90E2" : "transparent"}
                        style={{
                            transition: `fill 0.3s ease ${delay + 0.4}s`,
                            opacity: isVisible ? 1 : 0,
                            transformOrigin: "center",
                        }}
                    >
                        {isVisible && (
                            <animate
                                attributeName="r"
                                from="0"
                                to="14"
                                dur="0.4s"
                                begin={`${delay + 0.3}s`}
                                fill="freeze"
                            />
                        )}
                    </circle>
                    <path
                        d="M11 18.5l4.5 4.5L25 14"
                        fill="none"
                        stroke="white"
                        strokeWidth="2.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeDasharray="36"
                        strokeDashoffset={isVisible ? "0" : "36"}
                        style={{
                            transition: `stroke-dashoffset 0.5s ease ${delay + 0.5}s`
                        }}
                    />
                </svg>
            </Box>
            <Text
                color="gray.700"
                fontSize="md"
                lineHeight="1.6"
                transform={isVisible ? "translateX(0)" : "translateX(-8px)"}
                transition={`all 0.4s ease ${delay + 0.3}s`}
            >
                {text}
            </Text>
        </HStack>
    );
};

const AssessmentTopSection = React.memo(function AssessmentTopSection() { return (
    <Box bg="white" borderRadius="2xl" p={{ base: 5, md: 8 }} boxShadow="0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)" border="1px solid" borderColor="gray.100">
        <VStack spacing={6} align="stretch">
            <VStack spacing={2} align="flex-start">
                <Badge bg="linear-gradient(135deg, #4A90E2, #3DA2DD)" color="white" fontSize="xs" px={3} py={1} borderRadius="full">
                    Self-Assessment
                </Badge>
                <Heading fontSize={{ base: "2xl", md: "3xl" }} fontWeight="500" letterSpacing="-0.02em" color="gray.800">
                    Assessing Your Sleep
                </Heading>
            </VStack>

            <Text color="gray.600" lineHeight="1.9">
                Monitoring sleep in the weeks leading up to surgery helps identify issues early and supports better recovery. Sometimes the simplest questions are enough.
            </Text>

            <Box bg="gray.50" borderRadius="xl" p={6} border="1px solid" borderColor="gray.200">
                <Text fontWeight="600" color="gray.700" mb={4}>Ask yourself:</Text>
                <VStack align="flex-start" spacing={4}>
                    <AnimatedMoonCircle text={'"Do I feel refreshed when I wake up?"'} delay={0} />
                    <AnimatedMoonCircle text={'"Am I getting 7–9 hours most nights? Is my sleep broken?"'} delay={0.2} />
                    <AnimatedMoonCircle text={'"Do I need to nap during the day?"'} delay={0.4} />
                </VStack>
            </Box>

            <Text color="gray.500" lineHeight="1.8" fontSize="md">
                You can use a combination of simple self-tracking methods, questionnaires, and wearable devices to assess your sleep.
            </Text>
        </VStack>
    </Box>
); });

const SleepModule = () => {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);
    const totalSteps = 6;

    const [hospitalKitChecked, setHospitalKitChecked] = useState<boolean[]>(Array(9).fill(false));
    const [bedroomChecked, setBedroomChecked] = useState<boolean[]>(Array(5).fill(false));
    const [diaryOpen, setDiaryOpen] = useState(false);
    const [completeHovered, setCompleteHovered] = useState(false);
    const [completeClicked, setCompleteClicked] = useState(false);
    const [cpapChecked, setCpapChecked] = useState<boolean[]>(Array(5).fill(false));
    const celebrationPlayedRef = useRef(false);
    const [diaryEntries, setDiaryEntries] = useState({
        bedtime: "",
        wakeTime: "",
        timeToFallAsleep: "",
        awakenings: "",
        totalSleep: "",
        sleepQuality: 3,
        hadNap: false,
        napDuration: "",
        caffeine: false,
        alcohol: false,
        exercise: false,
        notes: ""
    });

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [currentStep]);

    const nextStep = () => {
        if (currentStep < totalSteps) {
            setCurrentStep(currentStep + 1);
        }
    };

    const prevStep = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
            setCompleteHovered(false);
            setCompleteClicked(false);
        }
    };

    const goToStep = (step: number) => {
        setCurrentStep(step);
    };

    const stepTitlesShort = ["Intro", "Science", "Assess", "Disorders", "Optimize", "Medication"];
    
    const ProgressIndicator = () => (
        <Box mb={10}>
            <HStack spacing={0} justify="center" position="relative" px={4}>
                {Array.from({ length: totalSteps }, (_, i) => (
                    <React.Fragment key={i}>
                        <VStack 
                            spacing={2} 
                            cursor="pointer" 
                            onClick={() => goToStep(i + 1)}
                            transition="all 0.3s ease"
                            _hover={{ transform: "translateY(-2px)" }}
                        >
                            <Box
                                w="40px"
                                h="40px"
                                borderRadius="full"
                                bg={i + 1 < currentStep ? "#4A90E2" : i + 1 === currentStep ? "linear-gradient(135deg, #4A90E2, #3DA2DD)" : "gray.200"}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                border="2px solid"
                                borderColor={i + 1 <= currentStep ? "#4A90E2" : "gray.300"}
                                transition="all 0.3s ease"
                                boxShadow={i + 1 === currentStep ? "0 0 20px rgba(74, 144, 226, 0.4)" : "none"}
                            >
                                {i + 1 < currentStep ? (
                                    <Icon as={FaCheck} w={4} h={4} color="white" animation={`${checkAnimation} 0.4s ease`} />
                                ) : (
                                    <Text color={i + 1 === currentStep ? "white" : "gray.500"} fontWeight="600" fontSize="sm">
                                        {i + 1}
                                    </Text>
                                )}
                            </Box>
                            <Text 
                                fontSize="xs" 
                                color={i + 1 === currentStep ? "#4A90E2" : i + 1 < currentStep ? "gray.600" : "gray.400"}
                                fontWeight={i + 1 === currentStep ? "500" : "400"}
                                transition="all 0.3s ease"
                                display={{ base: "none", md: "block" }}
                            >
                                {stepTitlesShort[i]}
                            </Text>
                        </VStack>
                        {i < totalSteps - 1 && (
                            <Box
                                flex={1}
                                h="2px"
                                mx={2}
                                mt="-20px"
                                bg={i + 1 < currentStep ? "#4A90E2" : "gray.200"}
                                transition="all 0.3s ease"
                                position="relative"
                                overflow="hidden"
                            >
                                {i + 1 < currentStep && (
                                    <Box
                                        position="absolute"
                                        top={0}
                                        left={0}
                                        right={0}
                                        bottom={0}
                                        bgGradient="linear(to-r, transparent, rgba(74, 144, 226, 0.3), transparent)"
                                        bgSize="200% 100%"
                                        animation={`${shimmer} 2s linear infinite`}
                                    />
                                )}
                            </Box>
                        )}
                    </React.Fragment>
                ))}
            </HStack>
            <Progress 
                value={(currentStep / totalSteps) * 100} 
                size="xs" 
                colorScheme="blue"
                bg="gray.100"
                borderRadius="full"
                mt={6}
            />
            <Text fontSize="xs" color="gray.500" textAlign="center" mt={2}>
                {Math.round((currentStep / totalSteps) * 100)}% Complete
            </Text>
        </Box>
    );

    const NavigationButtons = () => (
        <Flex 
            justify="space-between" 
            align="center"
            mt={12} 
            pt={8} 
            borderTop="1px solid" 
            borderColor="gray.200"
        >
            <Button
                variant="outline"
                color="gray.600"
                borderColor="gray.300"
                leftIcon={<ChevronLeftIcon />}
                onClick={prevStep}
                visibility={currentStep === 1 ? "hidden" : "visible"}
                _hover={{ borderColor: "#4A90E2", color: "#4A90E2", transform: "translateX(-2px)" }}
                transition="all 0.2s ease"
                borderRadius="full"
            >
                Previous
            </Button>
            <HStack spacing={2}>
                {Array.from({ length: totalSteps }, (_, i) => (
                    <Box
                        key={i}
                        w={currentStep === i + 1 ? "24px" : "8px"}
                        h="8px"
                        borderRadius="full"
                        bg={i + 1 <= currentStep ? "#4A90E2" : "gray.200"}
                        cursor="pointer"
                        onClick={() => goToStep(i + 1)}
                        transition="all 0.3s ease"
                        _hover={{ bg: "#4A90E2", opacity: 0.8 }}
                    />
                ))}
            </HStack>
            {currentStep < totalSteps ? (
                <Button
                    bg="linear-gradient(135deg, #4A90E2, #3DA2DD)"
                    color="white"
                    rightIcon={<ChevronRightIcon />}
                    onClick={nextStep}
                    _hover={{ transform: "translateX(2px)", boxShadow: "0 4px 15px rgba(74, 144, 226, 0.4)" }}
                    transition="all 0.2s ease"
                    borderRadius="full"
                    px={8}
                    fontWeight="500"
                >
                    Continue
                </Button>
            ) : (
                <Button
                    bg={completeClicked ? "linear-gradient(135deg, #38A169, #2F855A)" : "linear-gradient(135deg, #48BB78, #38A169)"}
                    color="white"
                    onClick={() => {
                        setCompleteClicked(true);
                        setTimeout(() => navigate("/all-modules"), 600);
                    }}
                    onMouseEnter={() => setCompleteHovered(true)}
                    onMouseLeave={() => { if (!completeClicked) setCompleteHovered(false); }}
                    _hover={{ boxShadow: "0 4px 20px rgba(72, 187, 120, 0.5)" }}
                    transition="all 0.3s ease"
                    borderRadius="full"
                    px={8}
                    fontWeight="500"
                    position="relative"
                    overflow="hidden"
                    transform={completeClicked ? "scale(0.97)" : "scale(1)"}
                >
                    <HStack spacing={2}>
                        <Text>Complete Module</Text>
                        <Box w="18px" h="18px" position="relative" display="flex" alignItems="center" justifyContent="center">
                            <svg width="18" height="18" viewBox="0 0 18 18">
                                <circle cx="9" cy="9" r="7.5" fill="none"
                                    stroke={completeClicked ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.5)"}
                                    strokeWidth="1.5"
                                    strokeDasharray="47.1"
                                    strokeDashoffset={completeHovered || completeClicked ? "0" : "47.1"}
                                    style={{ transition: "stroke-dashoffset 0.4s ease, stroke 0.2s ease" }}
                                />
                                {completeClicked && (
                                    <circle cx="9" cy="9" r="7" fill="rgba(255,255,255,0.25)" style={{ animation: "none" }}>
                                        <animate attributeName="r" from="0" to="7" dur="0.3s" fill="freeze" />
                                    </circle>
                                )}
                                <path d="M5.5 9.5l2 2L12.5 7"
                                    fill="none"
                                    stroke="white"
                                    strokeWidth={completeClicked ? "2" : "1.5"}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeDasharray="12"
                                    strokeDashoffset={completeHovered || completeClicked ? "0" : "12"}
                                    style={{ transition: `stroke-dashoffset ${completeClicked ? "0.3s" : "0.5s"} ease ${completeHovered && !completeClicked ? "0.2s" : "0s"}, stroke-width 0.2s ease` }}
                                />
                            </svg>
                        </Box>
                    </HStack>
                </Button>
            )}
        </Flex>
    );

    const [showConfetti, setShowConfetti] = useState(false);

    const FullPageConfetti = ({ isVisible }: { isVisible: boolean }) => {
        const colors = ["#FF3B5C", "#FF6B35", "#FFD23F", "#06D6A0", "#118AB2", "#8338EC", "#FF006E", "#3A86FF", "#FB5607", "#FFBE0B", "#48BB78", "#E040FB"];
        const particles = useRef(
            Array.from({ length: 60 }, (_, i) => ({
                x: 5 + Math.random() * 90,
                burstY: -10 - Math.random() * 30,
                fallY: 100 + Math.random() * 20,
                size: 6 + Math.random() * 10,
                color: colors[i % colors.length],
                delay: Math.random() * 0.8,
                duration: 2.5 + Math.random() * 2,
                sway: -30 + Math.random() * 60,
                rotation: Math.random() * 720,
                shape: i % 5,
            }))
        ).current;

        return (
            <Box position="fixed" top={0} left={0} w="100vw" h="100vh" pointerEvents="none" zIndex={9999}
                opacity={isVisible ? 1 : 0} transition="opacity 0.3s ease">
                {isVisible && particles.map((p, i) => {
                    const shapeStyle: React.CSSProperties = {
                        position: "absolute",
                        left: `${p.x}%`,
                        top: `${p.burstY}%`,
                        width: p.shape === 0 ? `${p.size}px` : p.shape === 1 ? `${p.size * 0.6}px` : `${p.size}px`,
                        height: p.shape === 1 ? `${p.size * 1.4}px` : `${p.size}px`,
                        backgroundColor: p.color,
                        borderRadius: p.shape === 0 ? "50%" : p.shape === 2 ? "2px" : p.shape === 3 ? "50% 0 50% 0" : "0",
                        animation: `confettiFall${i % 4} ${p.duration}s cubic-bezier(0.25, 0, 0.5, 1) ${p.delay}s forwards`,
                        transform: `rotate(${p.rotation}deg)`,
                        opacity: 0,
                    };
                    return <Box key={i} style={shapeStyle} />;
                })}
                <style>{`
                    ${[0,1,2,3].map(v => {
                        const swayA = -20 + v * 15;
                        const swayB = 10 - v * 8;
                        return `@keyframes confettiFall${v} {
                            0% { opacity: 0; transform: translateY(0) translateX(0) rotate(0deg) scale(0); }
                            8% { opacity: 1; transform: translateY(-5vh) translateX(${swayA * 0.3}px) rotate(90deg) scale(1.3); }
                            15% { opacity: 1; transform: translateY(5vh) translateX(${swayA}px) rotate(180deg) scale(1); }
                            50% { opacity: 0.9; transform: translateY(50vh) translateX(${swayB}px) rotate(${360 + v * 90}deg) scale(0.9); }
                            85% { opacity: 0.5; transform: translateY(90vh) translateX(${swayA * 0.5}px) rotate(${540 + v * 45}deg) scale(0.7); }
                            100% { opacity: 0; transform: translateY(110vh) translateX(${swayB * 0.8}px) rotate(${720 + v * 60}deg) scale(0.4); }
                        }`;
                    }).join("\n")}
                `}</style>
            </Box>
        );
    };

    const ModuleCompleteAnimation = () => {
        const ref = useRef<HTMLDivElement>(null);
        const [isVisible, setIsVisible] = useState(() => celebrationPlayedRef.current);
        useEffect(() => {
            if (celebrationPlayedRef.current) return;
            const observer = new IntersectionObserver(([entry]) => {
                if (entry.isIntersecting && !celebrationPlayedRef.current) {
                    celebrationPlayedRef.current = true;
                    setIsVisible(true);
                    setShowConfetti(true);
                    observer.disconnect();
                }
            }, { threshold: 0.3 });
            if (ref.current) observer.observe(ref.current);
            return () => observer.disconnect();
        }, []);

        return (
            <>
                <FullPageConfetti isVisible={showConfetti} />
                <Box ref={ref} bg="linear-gradient(135deg, #E0F7FA 0%, #B2EBF2 100%)" borderRadius="xl" p={8}
                    border="1px solid" borderColor="cyan.200" textAlign="center" position="relative" overflow="hidden">
                    <Box position="absolute" top="-20px" right="-20px" w="100px" h="100px"
                        bg="linear-gradient(135deg, #4A90E2, #3DA2DD)" borderRadius="full" opacity={0.1} />
                    <Box position="absolute" bottom="-30px" left="-30px" w="80px" h="80px"
                        bg="linear-gradient(135deg, #48BB78, #38A169)" borderRadius="full"
                        opacity={isVisible ? 0.08 : 0} transition="opacity 0.5s ease 0.8s" />

                    <Box w="120px" h="120px" mx="auto" mb={5} position="relative">
                        <svg width="120" height="120" viewBox="0 0 120 120" style={{ overflow: "visible" }}>
                            <circle cx="60" cy="60" r="42" fill="none"
                                stroke={isVisible ? "#48BB78" : "#CBD5E0"} strokeWidth="4" strokeLinecap="round"
                                strokeDasharray="264" strokeDashoffset={isVisible ? "0" : "264"}
                                style={{ transition: "stroke-dashoffset 0.8s cubic-bezier(0.65, 0, 0.35, 1) 0.1s, stroke 0.3s ease 0.1s" }} />
                            <circle cx="60" cy="60" r="38" fill="#48BB78"
                                opacity={isVisible ? 1 : 0}
                                transform={isVisible ? "scale(1)" : "scale(0)"}
                                style={{
                                    transition: "transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.45s, opacity 0.01s ease 0.45s",
                                    transformOrigin: "60px 60px"
                                }} />
                            <circle cx="60" cy="60" r="44" fill="none" stroke="#48BB78" strokeWidth="1"
                                opacity={isVisible ? 0.3 : 0}
                                strokeDasharray="276" strokeDashoffset={isVisible ? "0" : "276"}
                                style={{ transition: "stroke-dashoffset 1s ease 0.2s, opacity 0.3s ease 0.2s" }} />
                            <path d="M38 62l14 14L82 44" fill="none" stroke="white" strokeWidth="8"
                                strokeLinecap="round" strokeLinejoin="round"
                                strokeDasharray="64" strokeDashoffset={isVisible ? "0" : "64"}
                                style={{ transition: "stroke-dashoffset 0.6s cubic-bezier(0.65, 0, 0.35, 1) 0.7s" }} />
                        </svg>
                        <Box position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)"
                            w={isVisible ? "90px" : "0px"} h={isVisible ? "90px" : "0px"}
                            borderRadius="full" bg="rgba(72, 187, 120, 0.15)"
                            transition="all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.4s"
                            pointerEvents="none" />
                    </Box>

                    <Text fontWeight="700" fontSize={{ base: "xl", md: "2xl" }} letterSpacing="-0.02em"
                        bgGradient="linear(to-r, #2F855A, #48BB78, #38A169)" bgClip="text"
                        opacity={isVisible ? 1 : 0} transform={isVisible ? "translateY(0) scale(1)" : "translateY(20px) scale(0.9)"}
                        transition="all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.9s" mb={2}>
                        Module Complete!
                    </Text>
                    <Text color="gray.600" fontSize="sm" maxW="400px" mx="auto"
                        opacity={isVisible ? 1 : 0} transform={isVisible ? "translateY(0)" : "translateY(15px)"}
                        transition="all 0.5s ease 1.1s" lineHeight="1.7">
                        You've learned about sleep science, assessment tools, common disorders, and how to optimize your sleep for surgery.
                    </Text>
                    <Box h="3px" w={isVisible ? "60px" : "0px"} mx="auto" mt={4} borderRadius="full"
                        bg="linear-gradient(90deg, #48BB78, #4A90E2)" transition="width 0.6s ease 1.3s" />
                </Box>
            </>
        );
    };

    const AutoOpenExpandable = ({ title, children, isAdvanced = false, renderIcon, autoOpen = false }: { title: string; children: React.ReactNode; isAdvanced?: boolean; renderIcon?: (isExpanded: boolean) => React.ReactNode; autoOpen?: boolean }) => {
        const [autoIndex, setAutoIndex] = useState<number | undefined>(undefined);
        const sectionRef = useRef<HTMLDivElement>(null);
        const hasAutoOpened = useRef(false);

        useEffect(() => {
            if (!autoOpen || hasAutoOpened.current || !sectionRef.current) return;
            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting && !hasAutoOpened.current) {
                        hasAutoOpened.current = true;
                        setTimeout(() => setAutoIndex(0), 400);
                        observer.disconnect();
                    }
                },
                { threshold: 0.3 }
            );
            observer.observe(sectionRef.current);
            return () => observer.disconnect();
        }, [autoOpen]);

        return (
            <Box ref={sectionRef}>
                <Accordion allowToggle index={autoIndex !== undefined ? [autoIndex] : undefined} onChange={(idx) => setAutoIndex(typeof idx === 'number' ? idx : undefined)}>
                    <AccordionItem border="none" mb={4}>
                        {({ isExpanded }) => (
                            <>
                                <AccordionButton
                                    bg="gray.50"
                                    borderRadius="xl"
                                    py={4}
                                    px={5}
                                    border="1px solid"
                                    borderColor="gray.200"
                                    _hover={{ bg: "gray.100", borderColor: "#4A90E2" }}
                                    _expanded={{ bg: "white", borderBottomRadius: 0, borderColor: "#4A90E2" }}
                                    transition="all 0.2s ease"
                                >
                                    <HStack flex="1" textAlign="left" spacing={3}>
                                        {renderIcon && renderIcon(isExpanded)}
                                        <Text fontWeight="500" color="gray.700">{title}</Text>
                                        {isAdvanced && (
                                            <Badge bg="linear-gradient(135deg, #4A90E2, #3DA2DD)" color="white" fontSize="xs" px={2} borderRadius="full">
                                                Advanced
                                            </Badge>
                                        )}
                                    </HStack>
                                    <AccordionIcon color="gray.500" />
                                </AccordionButton>
                                <AccordionPanel
                                    bg="white"
                                    borderBottomRadius="xl"
                                    py={5}
                                    px={5}
                                    border="1px solid"
                                    borderTop="none"
                                    borderColor="#4A90E2"
                                    animation={`${fadeSlideIn} 0.3s ease`}
                                >
                                    {children}
                                </AccordionPanel>
                            </>
                        )}
                    </AccordionItem>
                </Accordion>
            </Box>
        );
    };

    const ExpandableSection = ({ title, children, isAdvanced = false, renderIcon }: { title: string; children: React.ReactNode; isAdvanced?: boolean; renderIcon?: (isExpanded: boolean) => React.ReactNode }) => (
        <Accordion allowToggle>
            <AccordionItem border="none" mb={4}>
                {({ isExpanded }) => (
                    <>
                        <AccordionButton
                            bg="gray.50"
                            borderRadius="xl"
                            py={4}
                            px={5}
                            border="1px solid"
                            borderColor="gray.200"
                            _hover={{ bg: "gray.100", borderColor: "#4A90E2" }}
                            _expanded={{ bg: "white", borderBottomRadius: 0, borderColor: "#4A90E2" }}
                            transition="all 0.2s ease"
                        >
                            <HStack flex="1" textAlign="left" spacing={3}>
                                {renderIcon && renderIcon(isExpanded)}
                                <Text fontWeight="500" color="gray.700">{title}</Text>
                                {isAdvanced && (
                                    <Badge bg="linear-gradient(135deg, #4A90E2, #3DA2DD)" color="white" fontSize="xs" px={2} borderRadius="full">
                                        Advanced
                                    </Badge>
                                )}
                            </HStack>
                            <AccordionIcon color="gray.500" />
                        </AccordionButton>
                        <AccordionPanel
                            bg="white"
                            borderBottomRadius="xl"
                            py={5}
                            px={5}
                            border="1px solid"
                            borderTop="none"
                            borderColor="#4A90E2"
                            animation={`${fadeSlideIn} 0.3s ease`}
                        >
                            {children}
                        </AccordionPanel>
                    </>
                )}
            </AccordionItem>
        </Accordion>
    );

    const DownloadCard = ({ title, description, url }: { title: string; description: string; url: string }) => (
        <Link href={url} isExternal _hover={{ textDecoration: "none" }}>
            <Box
                bg="white"
                borderRadius="xl"
                p={5}
                border="1px solid"
                borderColor="gray.200"
                transition="all 0.2s ease"
                _hover={{ 
                    borderColor: "#4A90E2", 
                    transform: "translateY(-3px)", 
                    boxShadow: "0 8px 25px rgba(74, 144, 226, 0.15)" 
                }}
            >
                <HStack justify="space-between">
                    <VStack align="flex-start" spacing={1}>
                        <Text fontWeight="500" color="gray.700">{title}</Text>
                        <Text fontSize="sm" color="gray.500">{description}</Text>
                    </VStack>
                    <Box
                        bg="linear-gradient(135deg, #4A90E2, #3DA2DD)"
                        borderRadius="lg"
                        p={3}
                        transition="all 0.2s ease"
                    >
                        <Icon as={FaDownload} color="white" w={4} h={4} />
                    </Box>
                </HStack>
            </Box>
        </Link>
    );

    const FloatingIcon = ({ icon, delay = 0, left, top }: { icon: any; delay?: number; left: string; top: string }) => (
        <Box
            position="absolute"
            left={left}
            top={top}
            animation={`${floatAnimation} 3s ease-in-out infinite`}
            style={{ animationDelay: `${delay}s` }}
            opacity={0.3}
        >
            <Icon as={icon} w={6} h={6} color="#4A90E2" />
        </Box>
    );

    const InteractiveChecklistItem = ({ 
        label, 
        checked, 
        onToggle, 
        icon 
    }: { 
        label: string; 
        checked: boolean; 
        onToggle: () => void; 
        icon: any;
    }) => (
        <HStack
            bg={checked ? "rgba(74, 144, 226, 0.08)" : "white"}
            borderRadius="xl"
            p={4}
            cursor="pointer"
            onClick={onToggle}
            transition="all 0.25s ease"
            border="2px solid"
            borderColor={checked ? "#4A90E2" : "gray.200"}
            _hover={{ 
                borderColor: "#4A90E2", 
                transform: "translateX(4px)",
                boxShadow: "0 4px 15px rgba(74, 144, 226, 0.1)"
            }}
        >
            <Box
                w={10}
                h={10}
                borderRadius="full"
                bg={checked ? "linear-gradient(135deg, #4A90E2, #3DA2DD)" : "gray.100"}
                display="flex"
                alignItems="center"
                justifyContent="center"
                transition="all 0.25s ease"
                boxShadow={checked ? "0 4px 12px rgba(74, 144, 226, 0.3)" : "none"}
            >
                {checked ? (
                    <Icon as={FaCheck} w={5} h={5} color="white" animation={`${checkAnimation} 0.4s ease`} />
                ) : (
                    <Icon as={icon} w={5} h={5} color="gray.400" />
                )}
            </Box>
            <Text
                color={checked ? "gray.400" : "gray.700"}
                textDecoration={checked ? "line-through" : "none"}
                flex={1}
                fontWeight={checked ? "400" : "500"}
                transition="all 0.2s ease"
            >
                {label}
            </Text>
            {checked && (
                <Badge 
                    bg="green.100" 
                    color="green.600" 
                    fontSize="xs" 
                    px={2} 
                    py={1} 
                    borderRadius="full"
                    animation={`${fadeSlideIn} 0.3s ease`}
                >
                    Done
                </Badge>
            )}
        </HStack>
    );

    const ContentCard = ({ children, noPadding = false }: { children: React.ReactNode; noPadding?: boolean }) => (
        <Box
            bg="white"
            borderRadius="2xl"
            p={noPadding ? 0 : { base: 6, md: 8 }}
            boxShadow="0 4px 20px rgba(0, 0, 0, 0.08)"
            border="1px solid"
            borderColor="gray.100"
            animation={`${fadeSlideIn} 0.5s ease`}
        >
            {children}
        </Box>
    );

    const Page1Introduction = () => (
        <VStack spacing={6} align="stretch">
            <Box
                bg="linear-gradient(135deg, #1a2332 0%, #0f1419 100%)"
                borderRadius="2xl"
                overflow="hidden"
                position="relative"
                h="280px"
                display="flex"
                alignItems="center"
                justifyContent="center"
            >
                <VStack spacing={4}>
                    <Box
                        w={20}
                        h={20}
                        borderRadius="full"
                        bg="whiteAlpha.200"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        cursor="pointer"
                        transition="all 0.3s ease"
                        _hover={{ transform: "scale(1.1)", bg: "whiteAlpha.300" }}
                    >
                        <Icon as={FaPlay} w={8} h={8} color="#4A90E2" />
                    </Box>
                    <Text color="whiteAlpha.600" fontSize="sm">Video Introduction Coming Soon</Text>
                </VStack>
                <FloatingIcon icon={FaMoon} left="10%" top="20%" delay={0} />
                <FloatingIcon icon={FaStar} left="85%" top="15%" delay={0.5} />
                <FloatingIcon icon={FaCloud} left="15%" top="70%" delay={1} />
                <FloatingIcon icon={FaStar} left="80%" top="75%" delay={1.5} />
            </Box>

            <ContentCard>
                <VStack spacing={5} align="flex-start">
                    <Heading fontSize={{ base: "2xl", md: "3xl" }} fontWeight="500" letterSpacing="-0.02em" color="gray.800">
                        Optimising your sleep
                    </Heading>
                    <Text color="gray.600" lineHeight="1.9" fontSize="lg">
                        Many people think sleep is when our brain 'turns off' and not much happens apart from our body getting to rest. In fact, sleep is a time when a number of essential processes take place to clear waste from the brain, keep hormones balanced, regulate inflammation, and support healthy thinking and memory.
                    </Text>
                    <Text color="gray.500" lineHeight="1.9">
                        Sleep is essential for general health, supporting the heart and blood vessels, immune system, brain function and emotions. Insufficient or poor-quality sleep increases the risk of complications after surgery and slows recovery.
                    </Text>
                </VStack>
            </ContentCard>

            <Box
                bg="linear-gradient(135deg, rgba(74, 144, 226, 0.08) 0%, rgba(74, 144, 226, 0.03) 100%)"
                borderRadius="xl"
                p={6}
                border="1px solid"
                borderColor="rgba(74, 144, 226, 0.2)"
            >
                <Text fontWeight="600" color="#3182CE" mb={4} fontSize="lg">What you'll learn in this module:</Text>
                <VStack align="flex-start" spacing={4}>
                    <AnimatedCheckItem text="Understand how sleep works and why it matters for surgery" delay={0} />
                    <AnimatedCheckItem text="Assess your current sleep quality" delay={0.15} />
                    <AnimatedCheckItem text="Identify potential sleep disorders that impact anaesthesia" delay={0.3} />
                    <AnimatedCheckItem text="Optimize your sleep before and during hospital stay" delay={0.45} />
                </VStack>
            </Box>
        </VStack>
    );

    const Page2SleepScience = () => (
        <VStack spacing={6} align="stretch">
            <ContentCard>
                <VStack spacing={6} align="stretch">
                    <VStack spacing={2} align="flex-start">
                        <Badge bg="linear-gradient(135deg, #4A90E2, #3DA2DD)" color="white" fontSize="xs" px={3} py={1} borderRadius="full">
                            Understanding Sleep
                        </Badge>
                        <Heading fontSize={{ base: "2xl", md: "3xl" }} fontWeight="500" letterSpacing="-0.02em" color="gray.800">
                            How Sleep Works
                        </Heading>
                    </VStack>

                    <Text color="gray.600" lineHeight="1.9" fontSize="lg">
                        Sleep moves through repeating cycles of light sleep, deep sleep, and dreaming sleep (REM), each lasting about 90 minutes.
                    </Text>

                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        <AnimatedSleepCard delay={0}>
                            <SleepTypeCard type="moon" />
                        </AnimatedSleepCard>
                        <AnimatedSleepCard delay={0.2}>
                            <SleepTypeCard type="star" />
                        </AnimatedSleepCard>
                    </SimpleGrid>

                    <Box>
                        <Text fontWeight="600" color="gray.700" mb={4}>Our sleep is guided by two main systems:</Text>
                        <VStack spacing={3} align="stretch">
                            <SleepSystemCard type="pressure" />
                            <SleepSystemCard type="clock" />
                        </VStack>
                    </Box>
                </VStack>
            </ContentCard>

            <HospitalChallengesBar />

            <ExpandableSection title="References" isAdvanced={false}>
                <Text color="gray.600" fontSize="sm" lineHeight="1.8">
                    Hillman DR, Carlucci M, Charchaflieh JG, et al. Society of Anesthesia and Sleep Medicine Position Paper on Patient Sleep During Hospitalization. Anesth Analg. 2023 Apr 1;136(4):814-824.
                </Text>
            </ExpandableSection>
        </VStack>
    );

    const Page3Assessment = () => (
        <VStack spacing={6} align="stretch">
            <AssessmentTopSection />

            {/* Interactive Sleep Diary - Cutesy/Cartoony Style with Book Animation */}
            <Box
                cursor="pointer"
                onClick={() => setDiaryOpen(!diaryOpen)}
            >
                {/* Diary Header Panel - Static (no 3D animation on panel) */}
                <Box
                    bg="linear-gradient(145deg, #E8D5FF 0%, #D4BFFF 30%, #C4A7FF 70%, #B794FF 100%)"
                    borderRadius="24px"
                    p={2}
                    boxShadow="0 8px 24px rgba(167, 139, 250, 0.3), 0 4px 12px rgba(0,0,0,0.1), inset 0 2px 4px rgba(255,255,255,0.3)"
                    _hover={{
                        boxShadow: "0 12px 40px rgba(167, 139, 250, 0.4), 0 8px 20px rgba(0,0,0,0.12)",
                        transform: "translateY(-2px)"
                    }}
                    transition="all 0.3s ease"
                >
                    <HStack
                        bg="linear-gradient(135deg, #A78BFA 0%, #8B5CF6 50%, #7C3AED 100%)"
                        borderRadius="20px"
                        p={5}
                        spacing={4}
                        justify="space-between"
                        position="relative"
                        overflow="hidden"
                    >
                        <HStack spacing={4}>
                            {/* Animated Book Icon Container - 3D animation ONLY here */}
                            <Box
                                position="relative"
                                sx={{ perspective: "800px", transformStyle: "preserve-3d" }}
                                w="60px"
                                h="60px"
                            >
                                {/* Mini Book Spine */}
                                <Box
                                    position="absolute"
                                    left="-4px"
                                    top="6px"
                                    bottom="6px"
                                    w="8px"
                                    bg="linear-gradient(90deg, #7C3AED 0%, #9F7AEA 50%, #7C3AED 100%)"
                                    borderRadius="2px 0 0 2px"
                                    animation={diaryOpen ? `${bookSpineGlow} 1s ease-in-out infinite` : "none"}
                                    transform={diaryOpen ? "translateX(-2px)" : "translateX(0)"}
                                    transition="all 0.4s ease"
                                    boxShadow="inset 1px 0 2px rgba(0,0,0,0.2)"
                                    zIndex={0}
                                />
                                
                                {/* Book Icon Box with 3D rotation */}
                                <Box
                                    bg="white"
                                    borderRadius="xl"
                                    position="relative"
                                    w="52px"
                                    h="52px"
                                    ml="4px"
                                    mt="4px"
                                    sx={{ 
                                        transformStyle: "preserve-3d",
                                        transformOrigin: "left center"
                                    }}
                                    transform={diaryOpen ? "rotateY(-20deg)" : "rotateY(0deg)"}
                                    transition="all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)"
                                    boxShadow={diaryOpen 
                                        ? "4px 4px 12px rgba(0,0,0,0.2), inset 0 1px 2px rgba(255,255,255,0.5)" 
                                        : "0 4px 12px rgba(0,0,0,0.15)"}
                                >
                                    {/* Animated Pages (flip effect on icon only) */}
                                    {diaryOpen && (
                                        <>
                                            {[0, 1, 2, 3, 4].map((i) => (
                                                <Box
                                                    key={i}
                                                    position="absolute"
                                                    top="4px"
                                                    left="4px"
                                                    right="4px"
                                                    bottom="4px"
                                                    bg="#FFFAF5"
                                                    borderRadius="lg"
                                                    sx={{ 
                                                        transformOrigin: "left center",
                                                        transformStyle: "preserve-3d",
                                                        backfaceVisibility: "hidden"
                                                    }}
                                                    animation={`${rapidPageFlip} ${0.3 + i * 0.06}s ease-out ${i * 0.04}s forwards`}
                                                    boxShadow="1px 1px 2px rgba(0,0,0,0.08)"
                                                    zIndex={5 - i}
                                                />
                                            ))}
                                        </>
                                    )}
                                    
                                    {/* Book emoji icons */}
                                    <Flex
                                        position="absolute"
                                        top={0}
                                        left={0}
                                        right={0}
                                        bottom={0}
                                        alignItems="center"
                                        justifyContent="center"
                                        zIndex={10}
                                    >
                                        <Text 
                                            fontSize="2xl"
                                            opacity={diaryOpen ? 0 : 1}
                                            transform={diaryOpen ? "scale(0.5)" : "scale(1)"}
                                            transition="all 0.3s ease"
                                            position="absolute"
                                        >
                                            📕
                                        </Text>
                                        <Text 
                                            fontSize="2xl"
                                            opacity={diaryOpen ? 1 : 0}
                                            transform={diaryOpen ? "scale(1.1)" : "scale(0.5)"}
                                            transition="all 0.3s ease 0.15s"
                                            position="absolute"
                                        >
                                            📖
                                        </Text>
                                    </Flex>
                                </Box>
                            </Box>
                            <VStack align="flex-start" spacing={0}>
                                <Text fontWeight="700" color="white" fontSize="xl" fontFamily="'Nunito', 'Comic Sans MS', cursive">
                                    My Sleep Diary ✨
                                </Text>
                                <Text color="whiteAlpha.900" fontSize="sm" fontFamily="'Nunito', sans-serif">
                                    Tap to {diaryOpen ? "close" : "open"} your diary!
                                </Text>
                            </VStack>
                        </HStack>
                        <Box
                            transform={diaryOpen ? "rotate(180deg) scale(1.1)" : "rotate(0deg) scale(1)"}
                            transition="all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)"
                            bg="whiteAlpha.200"
                            borderRadius="full"
                            p={2}
                        >
                            <ChevronDownIcon color="white" w={6} h={6} />
                        </Box>
                    </HStack>
                </Box>
            </Box>

            {/* Diary Content - Cute Pastel Notebook Style */}
            <Box
                maxH={diaryOpen ? "1200px" : "0px"}
                opacity={diaryOpen ? 1 : 0}
                overflow="hidden"
                transition="all 0.7s cubic-bezier(0.34, 1.56, 0.64, 1)"
                transform={diaryOpen ? "translateY(0) scale(1)" : "translateY(-20px) scale(0.95)"}
            >
                <Box
                    bg="linear-gradient(180deg, #FFF5F7 0%, #FED7E2 30%, #FECDD3 100%)"
                    borderRadius="3xl"
                    p={8}
                    mt={-3}
                    position="relative"
                    boxShadow="0 8px 32px rgba(236, 72, 153, 0.15), inset 0 2px 8px rgba(255,255,255,0.5)"
                    border="3px solid"
                    borderColor="pink.200"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Decorative elements */}
                    <Box position="absolute" top={4} right={4} fontSize="2xl" opacity={0.6}>🌙</Box>
                    <Box position="absolute" bottom={4} left={4} fontSize="xl" opacity={0.5}>💤</Box>
                    <Box position="absolute" top="50%" right={6} fontSize="lg" opacity={0.4}>⭐</Box>
                    
                    <VStack spacing={6} align="stretch">
                        <HStack spacing={3} borderBottom="3px dotted" borderColor="pink.300" pb={3}>
                            <Text fontSize="2xl">📝</Text>
                            <Text color="purple.600" fontWeight="700" fontSize="xl" fontFamily="'Nunito', 'Comic Sans MS', cursive">
                                Tonight's Sleep Entry
                            </Text>
                        </HStack>

                        {/* Time Entries - Cute Cards */}
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                            <Box bg="white" borderRadius="2xl" p={4} boxShadow="0 4px 12px rgba(0,0,0,0.08)" border="2px solid" borderColor="purple.100">
                                <HStack spacing={2} mb={2}>
                                    <Text fontSize="lg">🛏️</Text>
                                    <Text color="purple.600" fontSize="sm" fontWeight="600" fontFamily="'Nunito', sans-serif">Bedtime</Text>
                                </HStack>
                                <Input
                                    type="time"
                                    value={diaryEntries.bedtime}
                                    onChange={(e) => setDiaryEntries({...diaryEntries, bedtime: e.target.value})}
                                    bg="purple.50"
                                    border="2px solid"
                                    borderColor="purple.200"
                                    borderRadius="xl"
                                    color="purple.700"
                                    fontFamily="'Nunito', sans-serif"
                                    fontWeight="600"
                                    _focus={{ borderColor: "#8B5CF6", boxShadow: "0 0 0 3px rgba(139, 92, 246, 0.2)" }}
                                />
                            </Box>
                            <Box bg="white" borderRadius="2xl" p={4} boxShadow="0 4px 12px rgba(0,0,0,0.08)" border="2px solid" borderColor="yellow.100">
                                <HStack spacing={2} mb={2}>
                                    <Text fontSize="lg">☀️</Text>
                                    <Text color="yellow.600" fontSize="sm" fontWeight="600" fontFamily="'Nunito', sans-serif">Wake Time</Text>
                                </HStack>
                                <Input
                                    type="time"
                                    value={diaryEntries.wakeTime}
                                    onChange={(e) => setDiaryEntries({...diaryEntries, wakeTime: e.target.value})}
                                    bg="yellow.50"
                                    border="2px solid"
                                    borderColor="yellow.200"
                                    borderRadius="xl"
                                    color="yellow.700"
                                    fontFamily="'Nunito', sans-serif"
                                    fontWeight="600"
                                    _focus={{ borderColor: "#EAB308", boxShadow: "0 0 0 3px rgba(234, 179, 8, 0.2)" }}
                                />
                            </Box>
                        </SimpleGrid>

                        {diaryEntries.bedtime && diaryEntries.wakeTime && (
                            <Box bg="linear-gradient(135deg, #E0E7FF 0%, #EDE9FE 100%)" borderRadius="2xl" p={4}
                                border="2px solid" borderColor="indigo.200" textAlign="center">
                                <HStack spacing={2} justify="center" mb={2}>
                                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                                        <rect x="2" y="4" width="14" height="11" rx="2" stroke="#6366F1" strokeWidth="1.5" fill="none" />
                                        <line x1="5" y1="7" x2="13" y2="7" stroke="#6366F1" strokeWidth="1" opacity={0.4} />
                                        <line x1="5" y1="9.5" x2="13" y2="9.5" stroke="#6366F1" strokeWidth="1" opacity={0.4} />
                                        <line x1="5" y1="12" x2="10" y2="12" stroke="#6366F1" strokeWidth="1" opacity={0.4} />
                                        <circle cx="12" cy="4" r="2.5" fill="#6366F1" />
                                        <path d="M11 4h2M12 3v2" stroke="white" strokeWidth="0.8" strokeLinecap="round" />
                                    </svg>
                                    <Text color="indigo.600" fontSize="xs" fontWeight="700" fontFamily="'Nunito', sans-serif" letterSpacing="0.02em">
                                        Calculated Time Asleep
                                    </Text>
                                </HStack>
                                <Text color="indigo.800" fontSize="2xl" fontWeight="800" fontFamily="'Nunito', sans-serif">
                                    {(() => {
                                        const [bH, bM] = diaryEntries.bedtime.split(":").map(Number);
                                        const [wH, wM] = diaryEntries.wakeTime.split(":").map(Number);
                                        let diffMin = (wH * 60 + wM) - (bH * 60 + bM);
                                        if (diffMin <= 0) diffMin += 24 * 60;
                                        const hours = Math.floor(diffMin / 60);
                                        const mins = diffMin % 60;
                                        return `${hours}h ${mins > 0 ? `${mins}m` : ""}`;
                                    })()}
                                </Text>
                                <Text color="indigo.400" fontSize="xs" fontFamily="'Nunito', sans-serif" mt={1}>
                                    🛏️ {diaryEntries.bedtime} → ☀️ {diaryEntries.wakeTime}
                                </Text>
                            </Box>
                        )}

                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                            <Box bg="white" borderRadius="2xl" p={4} boxShadow="0 4px 12px rgba(0,0,0,0.08)" border="2px solid" borderColor="blue.100">
                                <HStack spacing={2} mb={2}>
                                    <Text fontSize="lg">😴</Text>
                                    <Text color="blue.600" fontSize="xs" fontWeight="600" fontFamily="'Nunito', sans-serif">Min to fall asleep</Text>
                                </HStack>
                                <NumberInput min={0} max={180}>
                                    <NumberInputField
                                        value={diaryEntries.timeToFallAsleep}
                                        onChange={(e) => setDiaryEntries({...diaryEntries, timeToFallAsleep: e.target.value})}
                                        bg="blue.50"
                                        border="2px solid"
                                        borderColor="blue.200"
                                        borderRadius="xl"
                                        color="blue.700"
                                        fontFamily="'Nunito', sans-serif"
                                        fontWeight="600"
                                        placeholder="0"
                                    />
                                </NumberInput>
                            </Box>
                            <Box bg="white" borderRadius="2xl" p={4} boxShadow="0 4px 12px rgba(0,0,0,0.08)" border="2px solid" borderColor="orange.100">
                                <HStack spacing={2} mb={2}>
                                    <Text fontSize="lg">👀</Text>
                                    <Text color="orange.600" fontSize="xs" fontWeight="600" fontFamily="'Nunito', sans-serif">Times woken up</Text>
                                </HStack>
                                <NumberInput min={0} max={20}>
                                    <NumberInputField
                                        value={diaryEntries.awakenings}
                                        onChange={(e) => setDiaryEntries({...diaryEntries, awakenings: e.target.value})}
                                        bg="orange.50"
                                        border="2px solid"
                                        borderColor="orange.200"
                                        borderRadius="xl"
                                        color="orange.700"
                                        fontFamily="'Nunito', sans-serif"
                                        fontWeight="600"
                                        placeholder="0"
                                    />
                                </NumberInput>
                            </Box>
                            <Box bg="white" borderRadius="2xl" p={4} boxShadow="0 4px 12px rgba(0,0,0,0.08)" border="2px solid" borderColor="green.100">
                                <HStack spacing={2} mb={2}>
                                    <Text fontSize="lg">⏰</Text>
                                    <Text color="green.600" fontSize="xs" fontWeight="600" fontFamily="'Nunito', sans-serif">Total hours slept</Text>
                                </HStack>
                                <NumberInput min={0} max={24} step={0.5}>
                                    <NumberInputField
                                        value={diaryEntries.totalSleep}
                                        onChange={(e) => setDiaryEntries({...diaryEntries, totalSleep: e.target.value})}
                                        bg="green.50"
                                        border="2px solid"
                                        borderColor="green.200"
                                        borderRadius="xl"
                                        color="green.700"
                                        fontFamily="'Nunito', sans-serif"
                                        fontWeight="600"
                                        placeholder="0"
                                    />
                                </NumberInput>
                            </Box>
                        </SimpleGrid>

                        {/* Sleep Quality Slider - Cute Rounded Design */}
                        <Box bg="white" borderRadius="3xl" p={6} boxShadow="0 6px 20px rgba(0,0,0,0.1)" border="3px solid" borderColor="purple.200">
                            <HStack spacing={3} mb={4}>
                                <Text fontSize="xl">💫</Text>
                                <Text color="purple.600" fontSize="md" fontWeight="700" fontFamily="'Nunito', sans-serif">
                                    Sleep Quality
                                </Text>
                                <Box bg="purple.100" borderRadius="full" px={4} py={1}>
                                    <Text color="purple.600" fontWeight="800" fontSize="lg" fontFamily="'Nunito', sans-serif">
                                        {diaryEntries.sleepQuality}/5 {diaryEntries.sleepQuality >= 4 ? "🌟" : diaryEntries.sleepQuality >= 3 ? "😊" : "😴"}
                                    </Text>
                                </Box>
                            </HStack>
                            <Box px={2}>
                                <Slider
                                    value={diaryEntries.sleepQuality}
                                    onChange={(val) => setDiaryEntries({...diaryEntries, sleepQuality: val})}
                                    min={1}
                                    max={5}
                                    step={1}
                                >
                                    <SliderTrack 
                                        bg="linear-gradient(90deg, #FED7E2 0%, #E9D5FF 50%, #C4B5FD 100%)" 
                                        h="16px" 
                                        borderRadius="full"
                                        boxShadow="inset 0 2px 4px rgba(0,0,0,0.1)"
                                    >
                                        <SliderFilledTrack 
                                            bg="linear-gradient(90deg, #EC4899 0%, #A855F7 50%, #8B5CF6 100%)" 
                                            borderRadius="full"
                                        />
                                    </SliderTrack>
                                    <SliderThumb 
                                        boxSize={10} 
                                        bg="white"
                                        border="4px solid"
                                        borderColor="purple.400"
                                        boxShadow="0 4px 12px rgba(139, 92, 246, 0.4)"
                                        _focus={{ boxShadow: "0 4px 16px rgba(139, 92, 246, 0.5)" }}
                                    >
                                        <Text fontSize="lg">🌙</Text>
                                    </SliderThumb>
                                </Slider>
                                <HStack justify="space-between" mt={3}>
                                    <Text color="pink.400" fontSize="sm" fontWeight="600" fontFamily="'Nunito', sans-serif">😫 Poor</Text>
                                    <Text color="purple.400" fontSize="sm" fontWeight="600" fontFamily="'Nunito', sans-serif">🤩 Amazing!</Text>
                                </HStack>
                            </Box>
                        </Box>

                        {/* Checkboxes - Cute Pill Buttons */}
                        <Box bg="white" borderRadius="2xl" p={5} boxShadow="0 4px 12px rgba(0,0,0,0.08)" border="2px solid" borderColor="pink.200">
                            <HStack spacing={2} mb={4}>
                                <Text fontSize="lg">📋</Text>
                                <Text color="pink.600" fontSize="md" fontWeight="700" fontFamily="'Nunito', sans-serif">Today's Factors</Text>
                            </HStack>
                            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
                                <Checkbox
                                    isChecked={diaryEntries.hadNap}
                                    onChange={(e) => setDiaryEntries({...diaryEntries, hadNap: e.target.checked})}
                                    colorScheme="purple"
                                    size="lg"
                                    borderRadius="full"
                                >
                                    <Text color="purple.600" fontSize="sm" fontWeight="600" fontFamily="'Nunito', sans-serif">😪 Nap</Text>
                                </Checkbox>
                                <Checkbox
                                    isChecked={diaryEntries.caffeine}
                                    onChange={(e) => setDiaryEntries({...diaryEntries, caffeine: e.target.checked})}
                                    colorScheme="purple"
                                    size="lg"
                                >
                                    <Text color="purple.600" fontSize="sm" fontWeight="600" fontFamily="'Nunito', sans-serif">☕ Caffeine</Text>
                                </Checkbox>
                                <Checkbox
                                    isChecked={diaryEntries.alcohol}
                                    onChange={(e) => setDiaryEntries({...diaryEntries, alcohol: e.target.checked})}
                                    colorScheme="purple"
                                    size="lg"
                                >
                                    <Text color="purple.600" fontSize="sm" fontWeight="600" fontFamily="'Nunito', sans-serif">🍷 Alcohol</Text>
                                </Checkbox>
                                <Checkbox
                                    isChecked={diaryEntries.exercise}
                                    onChange={(e) => setDiaryEntries({...diaryEntries, exercise: e.target.checked})}
                                    colorScheme="purple"
                                    size="lg"
                                >
                                    <Text color="purple.600" fontSize="sm" fontWeight="600" fontFamily="'Nunito', sans-serif">🏃 Exercise</Text>
                                </Checkbox>
                            </SimpleGrid>
                        </Box>

                        {/* Tip - Cute bubble */}
                        <Box 
                            bg="linear-gradient(135deg, #E9D5FF 0%, #F3E8FF 100%)" 
                            borderRadius="2xl" 
                            p={5} 
                            border="2px dashed" 
                            borderColor="purple.300"
                            position="relative"
                        >
                            <HStack spacing={3}>
                                <Text fontSize="2xl">💡</Text>
                                <Text color="purple.600" fontSize="sm" fontWeight="600" fontFamily="'Nunito', sans-serif">
                                    Keep this diary for 1-2 weeks to discover your sleep patterns! You got this! 💪
                                </Text>
                            </HStack>
                        </Box>
                    </VStack>
                </Box>
            </Box>

            <ExpandableSection title="Wearable Devices" isAdvanced>
                <VStack align="flex-start" spacing={3}>
                    <Text color="gray.600" lineHeight="1.7">
                        Wearables like watches, rings or sleep mats give you objective sleep data. Most track:
                    </Text>
                    <SimpleGrid columns={2} spacing={2} w="100%">
                        <Text color="gray.500" fontSize="sm">• Total sleep hours</Text>
                        <Text color="gray.500" fontSize="sm">• Sleep stages (light, deep, REM)</Text>
                        <Text color="gray.500" fontSize="sm">• Night-time awakenings</Text>
                        <Text color="gray.500" fontSize="sm">• Heart rate & HRV</Text>
                    </SimpleGrid>
                    <Text color="gray.400" fontSize="sm" mt={2}>
                        Common options: Fitbit, Apple Watch, Garmin, Oura Ring, WHOOP, Polar
                    </Text>
                </VStack>
            </ExpandableSection>

            <VStack spacing={3} align="stretch">
                <Text fontWeight="600" color="gray.700">Validated Assessment Tools:</Text>
                <DownloadCard 
                    title="Twenty Winks Sleep Questionnaire" 
                    description="Simple diagnostic tool from Healthify" 
                    url="https://healthify.nz/tools/t/twenty-winks-sleep-questionnaire"
                />
                <DownloadCard 
                    title="Pittsburgh Sleep Quality Index (PSQI)" 
                    description="Gold-standard validated tool (score >5 = poor quality)" 
                    url="https://melbournesleepsolutions.com.au/wp-content/uploads/Sleep-Solutions-Sleep-Quality-Assessment-2.pdf"
                />
                <DownloadCard 
                    title="Sleep Diary Template" 
                    description="Track your sleep for 1-2 weeks" 
                    url="http://yoursleep.aasmnet.org/pdf/sleepdiary.pdf"
                />
            </VStack>

            <Box mt={4} bg="linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 50%, #E0E7FF 100%)" borderRadius="xl" p={5}
                border="1px solid" borderColor="purple.200">
                <HStack spacing={3} mb={3} align="center">
                    <Box w="40px" h="40px" minW="40px" bg="linear-gradient(135deg, #7C3AED, #4A90E2)" borderRadius="lg"
                        display="flex" alignItems="center" justifyContent="center">
                        <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                            <path d="M11 2L13.5 7.5L19 8.5L15 12.5L16 18L11 15.5L6 18L7 12.5L3 8.5L8.5 7.5L11 2Z" fill="white" opacity={0.9} />
                        </svg>
                    </Box>
                    <VStack spacing={0} align="flex-start">
                        <Text fontWeight="700" fontSize="md" color="purple.800" letterSpacing="-0.01em">
                            Formal Sleep Study
                        </Text>
                        <Text fontSize="xs" color="purple.500" fontWeight="500">
                            Ordered by your healthcare provider
                        </Text>
                    </VStack>
                </HStack>
                <Text color="gray.700" fontSize="sm" lineHeight="1.8">
                    A formal sleep study (polysomnography) is the <Text as="span" fontWeight="700" color="purple.700">gold standard</Text> for diagnosing sleep disorders and comprehensively assessing your sleep. It records brain waves, oxygen levels, heart rate, and breathing patterns overnight to give your doctor the most detailed picture of your sleep health.
                </Text>
                <Text color="gray.500" fontSize="xs" mt={3} lineHeight="1.6">
                    If your healthcare team recommends a sleep study before surgery, it can help identify issues like sleep apnoea that may affect your anaesthetic and recovery plan.
                </Text>
            </Box>
        </VStack>
    );

    const Page4Disorders = () => (
        <VStack spacing={6} align="stretch">
            <ContentCard>
                <VStack spacing={6} align="stretch">
                    <VStack spacing={2} align="flex-start">
                        <Badge bg="linear-gradient(135deg, #4A90E2, #3DA2DD)" color="white" fontSize="xs" px={3} py={1} borderRadius="full">
                            Clinical Awareness
                        </Badge>
                        <Heading fontSize={{ base: "2xl", md: "3xl" }} fontWeight="500" letterSpacing="-0.02em" color="gray.800">
                            Common Sleep Disorders
                        </Heading>
                    </VStack>

                    <Text color="gray.600" lineHeight="1.9">
                        Knowing about insomnia, sleep apnoea, and restless legs syndrome before surgery is essential because untreated sleep disorders can increase risks such as breathing complications, higher pain levels, and slower recovery.
                    </Text>

                    <Box
                        bg="linear-gradient(135deg, #FEF2F2 0%, #FECACA 100%)"
                        borderRadius="xl"
                        p={5}
                        border="1px solid"
                        borderColor="red.200"
                    >
                        <HStack>
                            <Icon as={FaUser} color="red.500" w={4} h={4} />
                            <Text color="red.600" fontSize="sm" fontWeight="500">
                                Note: The conditions below are diagnosed only by your healthcare team.
                            </Text>
                        </HStack>
                    </Box>
                </VStack>
            </ContentCard>

            <ExpandableSection title="1. Insomnia">
                <VStack align="flex-start" spacing={4}>
                    <Text fontWeight="600" color="gray.700">Common Symptoms:</Text>
                    <SimpleGrid columns={1} spacing={2} w="100%">
                        <Text color="gray.600" fontSize="sm">• Difficulty falling asleep, staying asleep, or waking too early</Text>
                        <Text color="gray.600" fontSize="sm">• Feeling unrefreshed on waking</Text>
                        <Text color="gray.600" fontSize="sm">• Daytime fatigue, low mood, irritability</Text>
                        <Text color="gray.600" fontSize="sm">• Sleep that feels light, broken, or "not restorative"</Text>
                    </SimpleGrid>
                    <Link href="https://www.sleepprimarycareresources.org.au/questionnaires/isi" isExternal color="#4A90E2" fontSize="sm" fontWeight="500">
                        Screening Tool: Insomnia Severity Index (ISI) <ExternalLinkIcon mx={1} />
                    </Link>
                </VStack>
            </ExpandableSection>

            <ExpandableSection title="2. Obstructive Sleep Apnoea (OSA)">
                <VStack align="flex-start" spacing={4}>
                    <Box bg="yellow.50" p={3} borderRadius="lg" border="1px solid" borderColor="yellow.200">
                        <Text color="yellow.700" fontSize="sm" fontWeight="500">OSA is common and often undiagnosed; untreated OSA increases perioperative respiratory risk.</Text>
                    </Box>
                    <Text fontWeight="600" color="gray.700">Common Symptoms:</Text>
                    <SimpleGrid columns={1} spacing={2} w="100%">
                        <Text color="gray.600" fontSize="sm">• Loud snoring or witnessed breathing pauses during sleep</Text>
                        <Text color="gray.600" fontSize="sm">• Waking with choking, gasping, or dry mouth</Text>
                        <Text color="gray.600" fontSize="sm">• Excessive daytime sleepiness</Text>
                        <Text color="gray.600" fontSize="sm">• Morning headaches, poor concentration</Text>
                    </SimpleGrid>
                    <Link href="https://www.mdcalc.com/calc/3992/stop-bang-score-obstructive-sleep-apnea" isExternal color="#4A90E2" fontSize="sm" fontWeight="500">
                        Screening Tool: STOP-Bang Questionnaire <ExternalLinkIcon mx={1} />
                    </Link>
                </VStack>
            </ExpandableSection>

            <ExpandableSection title="3. Restless Legs Syndrome (RLS)">
                <VStack align="flex-start" spacing={4}>
                    <Text fontWeight="600" color="gray.700">Common Symptoms:</Text>
                    <SimpleGrid columns={1} spacing={2} w="100%">
                        <Text color="gray.600" fontSize="sm">• Uncomfortable sensations in legs (tingling, crawling, pulling)</Text>
                        <Text color="gray.600" fontSize="sm">• Symptoms begin or worsen in evening/night</Text>
                        <Text color="gray.600" fontSize="sm">• Symptoms improve with movement</Text>
                        <Text color="gray.600" fontSize="sm">• Trouble falling asleep due to leg discomfort</Text>
                    </SimpleGrid>
                    <Link href="https://biolincc.nhlbi.nih.gov/media/studies/masm/IRLS.pdf" isExternal color="#4A90E2" fontSize="sm" fontWeight="500">
                        Screening Tool: IRLSSG Rating Scale <ExternalLinkIcon mx={1} />
                    </Link>
                </VStack>
            </ExpandableSection>
        </VStack>
    );

    const Page5Optimization = () => {
        const hospitalKitItems = [
            { label: "Eye mask to block light", icon: FaMoon },
            { label: "Earplugs or earbuds to reduce noise", icon: FaCloud },
            { label: "Your own pillow or comfort item", icon: FaBed },
            { label: "Headphones for soothing music", icon: FaStar },
            { label: "Comfortable sleepwear", icon: FaMoon },
            { label: "Warm, non-caffeinated beverages (herbal tea)", icon: FaCloud },
            { label: "A small night light", icon: FaStar },
            { label: '"Do Not Disturb" sign', icon: FaMoon },
            { label: "CPAP machine if applicable", icon: FaBed },
        ];

        const bedroomItems = [
            { label: "Keep your sleep area dark", icon: FaMoon },
            { label: "Keep it quiet (earplugs, white noise)", icon: FaCloud },
            { label: "Know where the toilet is", icon: FaStar },
            { label: "Ask staff to dim lights earlier", icon: FaMoon },
            { label: "Keep essentials within reach", icon: FaBed },
        ];

        return (
            <VStack spacing={6} align="stretch">
                <ContentCard>
                    <VStack spacing={5} align="stretch">
                        <VStack spacing={2} align="flex-start">
                            <Badge bg="linear-gradient(135deg, #4A90E2, #3DA2DD)" color="white" fontSize="xs" px={3} py={1} borderRadius="full">
                                Take Action
                            </Badge>
                            <Heading fontSize={{ base: "2xl", md: "3xl" }} fontWeight="500" letterSpacing="-0.02em" color="gray.800">
                                Optimizing Your Sleep
                            </Heading>
                        </VStack>

                        <Text color="gray.600" lineHeight="1.9">
                            Many simple strategies can make a meaningful difference to your sleep when practiced consistently.
                        </Text>
                    </VStack>
                </ContentCard>

                <AutoOpenExpandable title="Good Sleep Habits" autoOpen>
                    <VStack align="flex-start" spacing={3}>
                        <Text color="gray.600" fontSize="sm">• Keep a consistent schedule—wake at the same time daily</Text>
                        <Text color="gray.600" fontSize="sm">• Create a relaxing 30–60 minute wind-down routine</Text>
                        <Text color="gray.600" fontSize="sm">• Limit screens in the hour before bed</Text>
                        <Text color="gray.600" fontSize="sm">• Watch caffeine, alcohol, and heavy meals after noon</Text>
                        <Text color="gray.600" fontSize="sm">• Make your environment cool, dark, and quiet</Text>
                        <Text color="gray.600" fontSize="sm">• Use the bed only for sleep and intimacy</Text>
                        <Text color="gray.600" fontSize="sm">• Go to bed only when sleepy</Text>
                        <Text color="gray.600" fontSize="sm">• If awake 20+ mins, get up and do a quiet activity</Text>
                        <Link href="https://www.sleephealthfoundation.org.au/sleep-topics/sleep-hygiene-good-sleep-habits" isExternal color="#4A90E2" fontSize="sm" mt={2} fontWeight="500">
                            Full resource from Sleep Health Foundation <ExternalLinkIcon mx={1} />
                        </Link>
                    </VStack>
                </AutoOpenExpandable>

                <Box
                    bg="linear-gradient(135deg, #EBF8FF 0%, #BEE3F8 100%)"
                    borderRadius="2xl"
                    p={6}
                    border="1px solid"
                    borderColor="blue.200"
                >
                    <HStack mb={4}>
                        <Box bg="#4A90E2" borderRadius="lg" p={2}>
                            <Icon as={FaBed} color="white" w={5} h={5} />
                        </Box>
                        <Heading fontSize="xl" fontWeight="600" color="gray.700">Your Hospital Sleep Kit</Heading>
                    </HStack>
                    <Text color="gray.600" fontSize="sm" mb={4}>
                        Check off items as you pack them:
                    </Text>
                    <VStack spacing={3} align="stretch">
                        {hospitalKitItems.map((item, index) => (
                            <InteractiveChecklistItem
                                key={index}
                                label={item.label}
                                icon={item.icon}
                                checked={hospitalKitChecked[index]}
                                onToggle={() => {
                                    const newChecked = [...hospitalKitChecked];
                                    newChecked[index] = !newChecked[index];
                                    setHospitalKitChecked(newChecked);
                                }}
                            />
                        ))}
                    </VStack>
                </Box>

                <ContentCard>
                    <Heading fontSize="lg" fontWeight="600" color="gray.700" mb={4}>
                        Hospital Bedroom Checklist
                    </Heading>
                    <VStack spacing={3} align="stretch">
                        {bedroomItems.map((item, index) => (
                            <InteractiveChecklistItem
                                key={index}
                                label={item.label}
                                icon={item.icon}
                                checked={bedroomChecked[index]}
                                onToggle={() => {
                                    const newChecked = [...bedroomChecked];
                                    newChecked[index] = !newChecked[index];
                                    setBedroomChecked(newChecked);
                                }}
                            />
                        ))}
                    </VStack>
                </ContentCard>

                <ExpandableSection title="Talking With Hospital Staff" renderIcon={(isExpanded) => (
                    <Box w="44px" h="44px" minW="44px" display="flex" alignItems="center" justifyContent="center" position="relative">
                        <svg width="44" height="44" viewBox="0 0 56 56" style={{ overflow: "visible" }}>
                            <defs>
                                <linearGradient id="talkHeadGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#4A90E2" />
                                    <stop offset="100%" stopColor="#2563EB" />
                                </linearGradient>
                            </defs>
                            <path d="M12 8 C12 8, 8 8, 6 12 C4 16, 4 20, 5 24 C6 27, 7 28, 7 30 L7 34 C7 36, 6 37, 6 38 C6 40, 8 40, 10 39 L12 37 C13 37, 14 38, 16 38 L19 38 C21 38, 22 37, 23 35 C24 34, 25 33, 26 32 C28 30, 29 28, 29 25 C29 23, 29 22, 28 20 L28 18 C28 16, 29 14, 29 12 C29 10, 28 8, 25 7 C22 6, 18 6, 15 7 C13 7.5, 12 8, 12 8Z"
                                fill="url(#talkHeadGrad)" />
                            <circle cx="16" cy="18" r="1.5" fill="white" opacity={0.8} />
                            <path d="M20 26 C21 27.5, 23 28, 25 27" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity={0.6} />
                            <path d="M27 15 C29 14, 30 15, 30 17 C30 19, 29 20, 27 20" fill="none" stroke="url(#talkHeadGrad)" strokeWidth="2" />

                            {[0, 1, 2].map((i) => (
                                <path key={`wave-${i}`}
                                    d={`M${33 + i * 5} ${20} Q${36 + i * 5} ${25}, ${33 + i * 5} ${30}`}
                                    fill="none" stroke="#4A90E2" strokeWidth={2.2 - i * 0.4} strokeLinecap="round"
                                    opacity={isExpanded ? 0 : 0.6 - i * 0.15}
                                    style={{ transition: "opacity 0.2s ease" }} />
                            ))}

                            {[0, 1, 2].map((i) => (
                                <path key={`wave-anim-${i}`}
                                    d={`M${33 + i * 5} ${20} Q${36 + i * 5} ${25}, ${33 + i * 5} ${30}`}
                                    fill="none" stroke="#3B82F6" strokeWidth={2.2 - i * 0.4} strokeLinecap="round"
                                    style={{
                                        transformOrigin: `${33 + i * 5}px 25px`,
                                        animation: isExpanded ? `${speechBurst1} 1s ease-out ${i * 0.15}s infinite` : "none",
                                        opacity: isExpanded ? 1 : 0
                                    }} />
                            ))}
                        </svg>
                    </Box>
                )}>
                    <VStack align="flex-start" spacing={4}>
                        <Text color="gray.600" fontSize="sm">
                            Advocating for your sleep needs is appropriate and welcomed:
                        </Text>
                        <Box bg="gray.50" borderRadius="lg" p={4} w="100%" border="1px solid" borderColor="gray.200">
                            <Text color="gray.600" fontSize="sm" fontStyle="italic">
                                "I'm trying to improve my sleep for recovery—could we minimise overnight interruptions unless necessary?"
                            </Text>
                        </Box>
                        <Box bg="gray.50" borderRadius="lg" p={4} w="100%" border="1px solid" borderColor="gray.200">
                            <Text color="gray.600" fontSize="sm" fontStyle="italic">
                                "Could my medications be timed to reduce nighttime waking?"
                            </Text>
                        </Box>
                        <Box bg="gray.50" borderRadius="lg" p={4} w="100%" border="1px solid" borderColor="gray.200">
                            <Text color="gray.600" fontSize="sm" fontStyle="italic">
                                "Is it possible to cluster observations together so I can have a longer sleep block?"
                            </Text>
                        </Box>
                    </VStack>
                </ExpandableSection>

                <ExpandableSection title="Do you use CPAP or a sleep device?" renderIcon={(isExpanded) => (
                    <Box w="44px" h="44px" minW="44px" display="flex" alignItems="center" justifyContent="center" position="relative">
                        <svg width="44" height="44" viewBox="0 0 60 48" style={{ overflow: "visible" }}>
                            <defs>
                                <linearGradient id="cpPillow2" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#C7D2FE" />
                                    <stop offset="100%" stopColor="#A5B4FC" />
                                </linearGradient>
                                <linearGradient id="cpSkin2" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#FDCFA1" />
                                    <stop offset="100%" stopColor="#F0B080" />
                                </linearGradient>
                                <linearGradient id="cpMask2" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#86EFAC" />
                                    <stop offset="100%" stopColor="#34D399" />
                                </linearGradient>
                                <linearGradient id="cpDevice2" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#60A5FA" />
                                    <stop offset="100%" stopColor="#3B82F6" />
                                </linearGradient>
                            </defs>

                            <path d="M2 28 Q2 20, 10 18 L28 16 Q34 16, 34 22 L34 32 Q34 36, 28 36 L10 36 Q2 36, 2 28Z" fill="url(#cpPillow2)" opacity={0.5} />

                            <ellipse cx="20" cy="22" rx="10" ry="11" fill="url(#cpSkin2)" />
                            <path d="M12 14 Q16 10, 22 11 Q27 10, 28 14 Q29 16, 27 17 L13 17 Q11 16, 12 14Z" fill="#6D5232" opacity={0.6} />
                            <ellipse cx="17" cy="20" rx="1" ry="0.5" fill="#5D4E37" opacity={0.4} />

                            <path d="M16 25 Q18 23, 20 24 Q22 23, 24 25 L25 28 Q25 31, 20 31 Q15 31, 15 28Z" fill="url(#cpMask2)" opacity={0.9} />
                            <path d="M16 26 Q18 25, 20 25.5 Q22 25, 24 26" fill="none" stroke="white" strokeWidth="0.8" opacity={0.4} />
                            <path d="M17 28 L23 28" fill="none" stroke="white" strokeWidth="0.6" opacity={0.3} />

                            <path d="M24 27 C26 27, 28 28, 30 29 Q34 31, 38 33 Q40 34, 42 35" fill="none" stroke="#34D399" strokeWidth="2.5" strokeLinecap="round" />

                            <rect x="40" y="30" width="16" height="14" rx="3" fill="url(#cpDevice2)" />
                            <rect x="42" y="32.5" width="12" height="9" rx="2" fill="#1E3A5F" opacity={0.8} />
                            <rect x="43.5" y="34" width="9" height="6" rx="1.5" fill="#34D399"
                                opacity={isExpanded ? 1 : 0.5}
                                style={{ animation: isExpanded ? `${cpapPulse} 1.5s ease-in-out infinite` : "none" }} />
                            <circle cx="44" cy="44" r="1" fill="#6EE7B7" opacity={0.6} />

                            {[0, 1, 2].map((i) => (
                                <path key={`air-static-${i}`}
                                    d={`M${30 + i * 3} ${25 - i * 2} L${34 + i * 3} ${23 - i * 2}`}
                                    fill="none" stroke="#86EFAC" strokeWidth={1.8 - i * 0.3} strokeLinecap="round"
                                    opacity={isExpanded ? 0 : 0.4 - i * 0.1}
                                    style={{ transition: "opacity 0.2s ease" }} />
                            ))}

                            {[0, 1, 2].map((i) => (
                                <path key={`air-anim-${i}`}
                                    d={`M${34 + i * 3} ${23 - i * 2} L${30 + i * 3} ${25 - i * 2}`}
                                    fill="none" stroke="#34D399" strokeWidth={1.8 - i * 0.3} strokeLinecap="round"
                                    style={{
                                        transformOrigin: `${34 + i * 3}px ${23 - i * 2}px`,
                                        animation: isExpanded ? `${cpapSuck1} 1s ease-in ${i * 0.2}s infinite` : "none",
                                        opacity: isExpanded ? 0.8 : 0
                                    }} />
                            ))}
                        </svg>
                    </Box>
                )}>
                    <Box bg="linear-gradient(135deg, #EBF4FF 0%, #DBEAFE 50%, #E0F2FE 100%)" borderRadius="xl" p={5}>
                        <Text color="gray.700" fontSize="sm" fontWeight="600" mb={4} lineHeight="1.7">
                            Patients with sleep apnoea should prepare a travel CPAP plan:
                        </Text>
                        <VStack align="stretch" spacing={3}>
                            {[
                                "Pack your CPAP machine, mask, tubing, and charger.",
                                "Bring a backup mask cushion if available.",
                                "Label equipment with your name and contact details.",
                                "Tell your surgical and nursing team in advance so your CPAP can be set up safely after surgery.",
                                "Some hospitals provide humidifiers—ask if you can bring your own if preferred."
                            ].map((item, idx) => (
                                <HStack key={idx} spacing={3} align="flex-start" cursor="pointer"
                                    onClick={() => {
                                        const updated = [...cpapChecked];
                                        updated[idx] = !updated[idx];
                                        setCpapChecked(updated);
                                    }}
                                    bg={cpapChecked[idx] ? "rgba(74, 144, 226, 0.12)" : "white"}
                                    borderRadius="lg" p={3} border="1px solid"
                                    borderColor={cpapChecked[idx] ? "blue.300" : "blue.100"}
                                    transition="all 0.3s ease"
                                    _hover={{ borderColor: "blue.300", bg: cpapChecked[idx] ? "rgba(74, 144, 226, 0.15)" : "blue.50" }}>
                                    <Box w="24px" h="24px" minW="24px" display="flex" alignItems="center" justifyContent="center" mt="1px">
                                        <svg width="24" height="24" viewBox="0 0 24 24">
                                            <circle cx="12" cy="12" r="10" fill="none"
                                                stroke={cpapChecked[idx] ? "#4A90E2" : "#CBD5E0"} strokeWidth="2"
                                                style={{ transition: "stroke 0.3s ease" }} />
                                            <circle cx="12" cy="12" r="8"
                                                fill={cpapChecked[idx] ? "#4A90E2" : "transparent"}
                                                transform={cpapChecked[idx] ? "scale(1)" : "scale(0)"}
                                                style={{ transition: "transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), fill 0.2s ease", transformOrigin: "12px 12px" }} />
                                            <path d="M8 12.5l2.5 2.5L16 10" fill="none" stroke="white" strokeWidth="2"
                                                strokeLinecap="round" strokeLinejoin="round"
                                                strokeDasharray="12" strokeDashoffset={cpapChecked[idx] ? "0" : "12"}
                                                style={{ transition: "stroke-dashoffset 0.3s ease 0.1s" }} />
                                        </svg>
                                    </Box>
                                    <Text color={cpapChecked[idx] ? "blue.700" : "gray.600"} fontSize="sm" lineHeight="1.6"
                                        textDecoration={cpapChecked[idx] ? "line-through" : "none"}
                                        transition="all 0.3s ease">
                                        {item}
                                    </Text>
                                </HStack>
                            ))}
                        </VStack>
                        <Text color="blue.400" fontSize="xs" mt={3} textAlign="center">
                            {cpapChecked.filter(Boolean).length} of {cpapChecked.length} items checked
                        </Text>
                    </Box>
                </ExpandableSection>

            </VStack>
        );
    };

    const Page6Medication = () => {
        return (
            <VStack spacing={6} align="stretch">
                <ContentCard>
                    <VStack spacing={6} align="stretch">
                        <VStack spacing={2} align="flex-start">
                            <Badge bg="linear-gradient(135deg, #7C3AED, #6D28D9)" color="white" fontSize="xs" px={3} py={1} borderRadius="full">
                                Important Guidance
                            </Badge>
                            <Heading fontSize={{ base: "2xl", md: "3xl" }} fontWeight="500" letterSpacing="-0.02em" color="gray.800">
                                What About Sleep Medication?
                            </Heading>
                        </VStack>

                        <Box bg="linear-gradient(135deg, #EDE9FE 0%, #E0E7FF 100%)" borderRadius="xl" p={5} border="1px solid" borderColor="purple.200">
                            <HStack spacing={3} align="flex-start">
                                <Box w="36px" h="36px" minW="36px" bg="linear-gradient(135deg, #7C3AED, #4A90E2)" borderRadius="lg"
                                    display="flex" alignItems="center" justifyContent="center" mt="2px">
                                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                                        <path d="M9 2v6l4 2" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                                        <circle cx="9" cy="9" r="7" stroke="white" strokeWidth="1.5" fill="none" />
                                    </svg>
                                </Box>
                                <Text color="gray.700" fontSize="sm" lineHeight="1.8" fontWeight="500">
                                    It is important to discuss sleep problems with your GP or a sleep specialist, who can review current medications, identify contributing health factors, and recommend safer alternatives where appropriate.
                                </Text>
                            </HStack>
                        </Box>

                        <Text color="gray.600" fontSize="sm" lineHeight="1.9">
                            In some cases, non-medication strategies — sleep hygiene, breathing exercises, cognitive-behavioural sleep strategies — may reduce or remove the need for sedatives.
                        </Text>
                    </VStack>
                </ContentCard>

                <ContentCard>
                    <VStack spacing={5} align="stretch">
                        <HStack spacing={3} align="center">
                            <Box w="40px" h="40px" minW="40px" bg="linear-gradient(135deg, #DC2626, #EF4444)" borderRadius="lg"
                                display="flex" alignItems="center" justifyContent="center">
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M10 3L17 17H3L10 3Z" stroke="white" strokeWidth="1.5" fill="none" strokeLinejoin="round" />
                                    <path d="M10 8v4M10 14v0.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                                </svg>
                            </Box>
                            <VStack spacing={0} align="flex-start">
                                <Text fontWeight="700" fontSize="md" color="gray.800">Risks of Common Sleep Medications</Text>
                                <Text fontSize="xs" color="gray.500">Benzodiazepines & Z-drugs around surgery</Text>
                            </VStack>
                        </HStack>

                        <Text color="gray.600" fontSize="sm" lineHeight="1.9">
                            Sleep medications — particularly <Text as="span" fontWeight="600" color="red.600">benzodiazepines</Text> (e.g., temazepam, diazepam) and <Text as="span" fontWeight="600" color="red.600">non-benzodiazepine hypnotics or "Z-drugs"</Text> (e.g., zolpidem, zopiclone) — are commonly prescribed before surgery and during hospital stays.
                        </Text>

                        <Text color="gray.600" fontSize="sm" lineHeight="1.9">
                            However, they provide only <Text as="span" fontWeight="600">modest improvements in sleep</Text> while carrying significant risks, especially around the time of surgery.
                        </Text>

                        <Box bg="linear-gradient(135deg, #FEF2F2 0%, #FECACA 100%)" borderRadius="xl" p={5} border="1px solid" borderColor="red.200">
                            <Text fontWeight="600" fontSize="sm" color="red.700" mb={3}>
                                Starting these medications preoperatively or during admission is linked with:
                            </Text>
                            <VStack align="stretch" spacing={2}>
                                {["Falls", "Breathing problems", "Delirium", "Confusion", "Prolonged hospital stay"].map((risk, idx) => (
                                    <HStack key={idx} spacing={3}>
                                        <Box w="6px" h="6px" minW="6px" borderRadius="full" bg="red.400" />
                                        <Text color="red.700" fontSize="sm" fontWeight="500">{risk}</Text>
                                    </HStack>
                                ))}
                            </VStack>
                        </Box>
                    </VStack>
                </ContentCard>

                <ContentCard>
                    <VStack spacing={5} align="stretch">
                        <HStack spacing={3} align="center">
                            <Box w="40px" h="40px" minW="40px" bg="linear-gradient(135deg, #059669, #10B981)" borderRadius="lg"
                                display="flex" alignItems="center" justifyContent="center">
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <circle cx="10" cy="10" r="7" stroke="white" strokeWidth="1.5" fill="none" />
                                    <path d="M7 10l2 2 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </Box>
                            <VStack spacing={0} align="flex-start">
                                <Text fontWeight="700" fontSize="md" color="gray.800">Safer Alternatives</Text>
                                <Text fontSize="xs" color="gray.500">Melatonin & herbal options</Text>
                            </VStack>
                        </HStack>

                        <Box bg="linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)" borderRadius="xl" p={5} border="1px solid" borderColor="green.200">
                            <Text fontWeight="600" fontSize="sm" color="green.800" mb={2}>Melatonin</Text>
                            <Text color="gray.700" fontSize="sm" lineHeight="1.9">
                                Melatonin is often a preferred alternative in the perioperative setting: it can <Text as="span" fontWeight="600" color="green.700">reduce preoperative anxiety</Text>, may help <Text as="span" fontWeight="600" color="green.700">prevent delirium in older adults</Text>, and has a much <Text as="span" fontWeight="600" color="green.700">safer cognitive profile</Text> than benzodiazepines, with minimal next-day sedation.
                            </Text>
                        </Box>

                        <Box bg="linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)" borderRadius="xl" p={5} border="1px solid" borderColor="yellow.200">
                            <Text fontWeight="600" fontSize="sm" color="yellow.800" mb={2}>Herbal Agents (e.g., Valerian)</Text>
                            <Text color="gray.700" fontSize="sm" lineHeight="1.9">
                                Herbal agents, such as valerian, are sometimes used after surgery and may assist with sleep, but evidence is limited and quality/interaction concerns mean they should be used <Text as="span" fontWeight="600" color="yellow.700">cautiously and only under professional guidance</Text>.
                            </Text>
                        </Box>
                    </VStack>
                </ContentCard>

                <ContentCard>
                    <Box bg="linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 50%, #E0E7FF 100%)" borderRadius="xl" p={6} border="1px solid" borderColor="blue.200">
                        <HStack spacing={3} align="flex-start">
                            <Box w="36px" h="36px" minW="36px" bg="linear-gradient(135deg, #4A90E2, #3DA2DD)" borderRadius="full"
                                display="flex" alignItems="center" justifyContent="center" mt="2px">
                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                                    <path d="M9 4v5l3 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                                    <circle cx="9" cy="9" r="7" stroke="white" strokeWidth="1.5" fill="none" />
                                    <path d="M4 15l-1 2M14 15l1 2" stroke="white" strokeWidth="1.2" strokeLinecap="round" />
                                </svg>
                            </Box>
                            <Text color="gray.700" fontSize="sm" lineHeight="1.9" fontWeight="500">
                                Overall, sleep medications should be approached thoughtfully before surgery — balancing benefits with risks — and used as part of a broader plan that includes behavioural, environmental, and mind-body sleep strategies to support safe and restorative rest during recovery.
                            </Text>
                        </HStack>
                    </Box>
                </ContentCard>

                <ModuleCompleteAnimation />
            </VStack>
        );
    };

    const page1 = useMemo(() => <Page1Introduction />, []);
    const page2 = useMemo(() => <Page2SleepScience />, []);
    const page4 = useMemo(() => <Page4Disorders />, []);

    const renderCurrentPage = () => {
        switch (currentStep) {
            case 1: return page1;
            case 2: return page2;
            case 3: return Page3Assessment();
            case 4: return page4;
            case 5: return Page5Optimization();
            case 6: return Page6Medication();
            default: return page1;
        }
    };

    const stepTitles = [
        "Introduction",
        "Sleep Science",
        "Assessment",
        "Disorders",
        "Optimization",
        "Medication"
    ];

    return (
        <Box bg="linear-gradient(180deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)" minHeight="100vh">
            <Box 
                bg="linear-gradient(135deg, #0f1419 0%, #1a2332 100%)"
                borderBottom="1px solid"
                borderColor="whiteAlpha.200"
                position="sticky"
                top={0}
                zIndex={100}
            >
                <Container maxW="900px" px={{ base: 4, md: 6 }}>
                    <Flex 
                        justify="space-between" 
                        align="center" 
                        minH={{ base: "44px", md: "48px" }}
                    >
                        <HStack spacing={2} cursor="pointer" onClick={() => navigate('/')}>
                            <Image 
                                src="/surgready/logo.png" 
                                alt="SurgReady" 
                                h={{ base: "32px", md: "40px" }}
                                objectFit="contain"
                            />
                        </HStack>
                        <HStack spacing={{ base: 2, md: 4 }}>
                            <Button
                                variant="ghost"
                                fontSize="xs"
                                fontWeight="400"
                                letterSpacing="0.1em"
                                textTransform="uppercase"
                                color="whiteAlpha.900"
                                onClick={() => navigate('/all-modules')}
                                _hover={{ color: "#4A90E2", bg: "transparent" }}
                                size="sm"
                            >
                                All Modules
                            </Button>
                            <Button
                                variant="ghost"
                                fontSize="xs"
                                fontWeight="400"
                                letterSpacing="0.1em"
                                textTransform="uppercase"
                                color="whiteAlpha.900"
                                onClick={() => navigate('/member-homepage')}
                                _hover={{ color: "#4A90E2", bg: "transparent" }}
                                size="sm"
                            >
                                Dashboard
                            </Button>
                        </HStack>
                    </Flex>
                </Container>
            </Box>

            <Box
                bg="linear-gradient(135deg, #0f1419 0%, #1a2332 100%)"
                py={8}
                pb={12}
            >
                <Container maxW="900px" px={{ base: 4, md: 6 }}>
                    <VStack spacing={2} align="center">
                        <Badge
                            bg="whiteAlpha.100"
                            color="whiteAlpha.700"
                            fontSize="xs"
                            px={3}
                            py={1}
                            borderRadius="full"
                            textTransform="uppercase"
                            letterSpacing="0.1em"
                        >
                            Module 1
                        </Badge>
                        <HStack spacing={2}>
                            <Icon as={FaMoon} color="#4A90E2" w={5} h={5} />
                            <Heading
                                fontSize={{ base: "xl", md: "2xl" }}
                                fontWeight="400"
                                letterSpacing="-0.02em"
                                color="white"
                            >
                                Sleep
                            </Heading>
                        </HStack>
                        <Text color="whiteAlpha.500" fontSize="sm">
                            {stepTitles[currentStep - 1]}
                        </Text>
                    </VStack>
                </Container>
            </Box>

            <Container maxW="900px" px={{ base: 4, md: 6 }} py={{ base: 6, md: 10 }} mt={-6}>
                <Box
                    bg="white"
                    borderRadius="2xl"
                    p={{ base: 4, md: 8 }}
                    boxShadow="0 10px 40px rgba(0, 0, 0, 0.1)"
                    border="1px solid"
                    borderColor="gray.100"
                >
                    <ProgressIndicator />

                    {renderCurrentPage()}

                    <NavigationButtons />
                </Box>
            </Container>
        </Box>
    );
};

export default SleepModule;
